import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Tooltip,
  Snackbar,
  Alert,
  useTheme,
  alpha,
  TextField,
  InputAdornment,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Paper,
  CircularProgress,
  Chip,
  Modal,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  ToggleButton,
  ToggleButtonGroup,
  Select,
  FormControl,
  InputLabel,
  Checkbox,
  Stack,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';

import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';

import SearchIcon from '@mui/icons-material/Search';
import DateRangeIcon from '@mui/icons-material/DateRange';
import TodayIcon from '@mui/icons-material/Today';
import EventNoteIcon from '@mui/icons-material/EventNote';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import HistoryIcon from '@mui/icons-material/History';
import AddIcon from '@mui/icons-material/Add';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import BugReportIcon from '@mui/icons-material/BugReport';
import HelpIcon from '@mui/icons-material/Help';
import AssignmentIcon from '@mui/icons-material/Assignment';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import DeleteIcon from '@mui/icons-material/Delete';
import WarningIcon from '@mui/icons-material/Warning';
import WbSunnyIcon from '@mui/icons-material/WbSunny';
import NightsStayIcon from '@mui/icons-material/NightsStay';
import WatchLaterIcon from '@mui/icons-material/WatchLater';
import FirstPageIcon from '@mui/icons-material/FirstPage';
import LastPageIcon from '@mui/icons-material/LastPage';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';

// Authentication imports
import { useAuth } from '../../context/AuthContext';

import useDefectsData from './hooks/useDefectsData';
import useDefectStats from './hooks/useDefectStats';
import useRealTimeUpdates from '../../hooks/useRealTimeUpdates';
import { envisDevicesApi, envisFindingsApi, classificationApi, networksApi, sectionsApi } from '../../services/api';
import api from '../../services/api'; // Import base api instance for authenticated requests
import DefectValidationModal from './modals/DefectValidationModal';
import DuplicationModal from './modals/DuplicationModal';
import DefectsFilters from './components/DefectsFilters';
import TimeProgressBar from './components/TimeProgressBar';
import RepeatedFindingsSummary from './components/RepeatedFindingsSummary';
import { IssueWorkOrderModal } from '../WorkOrders';
import DisciplineAccessControl from '../common/DisciplineAccessControl';

// Custom Pagination Actions Component
const TablePaginationActions = (props) => {
  const theme = useTheme();
  const { count, page, rowsPerPage, onPageChange } = props;

  const totalPages = Math.ceil(count / rowsPerPage);
  const isFirstPage = page === 0;
  const isLastPage = page >= totalPages - 1;

  const handleFirstPageButtonClick = (event) => {
    onPageChange(event, 0);
  };

  const handleBackButtonClick = (event) => {
    onPageChange(event, page - 1);
  };

  const handleNextButtonClick = (event) => {
    onPageChange(event, page + 1);
  };

  const handleLastPageButtonClick = (event) => {
    onPageChange(event, Math.max(0, totalPages - 1));
  };

  // Keyboard navigation
  const handleKeyDown = (event, action) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      action(event);
    }
  };

  return (
    <Box sx={{ 
      flexShrink: 0, 
      ml: 2.5,
      display: 'flex',
      alignItems: 'center',
      gap: 0.5
    }}>
      <IconButton
        onClick={handleFirstPageButtonClick}
        onKeyDown={(e) => handleKeyDown(e, handleFirstPageButtonClick)}
        disabled={isFirstPage}
        aria-label={`Go to first page (Page 1 of ${totalPages})`}
        title={`First page${isFirstPage ? ' - Already on first page' : ''}`}
        size="small"
        sx={{ 
          backgroundColor: isFirstPage ? 'transparent' : alpha(theme.palette.primary.main, 0.04),
          color: isFirstPage ? theme.palette.action.disabled : theme.palette.primary.main,
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            backgroundColor: isFirstPage ? 'transparent' : alpha(theme.palette.primary.main, 0.12),
            transform: isFirstPage ? 'none' : 'scale(1.05)',
          },
          '&:focus': {
            outline: `2px solid ${theme.palette.primary.main}`,
            outlineOffset: 2,
          }
        }}
      >
        <FirstPageIcon fontSize="small" />
      </IconButton>
      
      <IconButton
        onClick={handleBackButtonClick}
        onKeyDown={(e) => handleKeyDown(e, handleBackButtonClick)}
        disabled={isFirstPage}
        aria-label={`Go to previous page${isFirstPage ? ' - Already on first page' : ` (Page ${page})`}`}
        title={`Previous page${isFirstPage ? ' - Already on first page' : ''}`}
        size="small"
        sx={{ 
          backgroundColor: isFirstPage ? 'transparent' : alpha(theme.palette.primary.main, 0.04),
          color: isFirstPage ? theme.palette.action.disabled : theme.palette.primary.main,
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            backgroundColor: isFirstPage ? 'transparent' : alpha(theme.palette.primary.main, 0.12),
            transform: isFirstPage ? 'none' : 'scale(1.05)',
          },
          '&:focus': {
            outline: `2px solid ${theme.palette.primary.main}`,
            outlineOffset: 2,
          }
        }}
      >
        <KeyboardArrowLeft fontSize="small" />
      </IconButton>
      
      <IconButton
        onClick={handleNextButtonClick}
        onKeyDown={(e) => handleKeyDown(e, handleNextButtonClick)}
        disabled={isLastPage}
        aria-label={`Go to next page${isLastPage ? ' - Already on last page' : ` (Page ${page + 2})`}`}
        title={`Next page${isLastPage ? ' - Already on last page' : ''}`}
        size="small"
        sx={{ 
          backgroundColor: isLastPage ? 'transparent' : alpha(theme.palette.primary.main, 0.04),
          color: isLastPage ? theme.palette.action.disabled : theme.palette.primary.main,
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            backgroundColor: isLastPage ? 'transparent' : alpha(theme.palette.primary.main, 0.12),
            transform: isLastPage ? 'none' : 'scale(1.05)',
          },
          '&:focus': {
            outline: `2px solid ${theme.palette.primary.main}`,
            outlineOffset: 2,
          }
        }}
      >
        <KeyboardArrowRight fontSize="small" />
      </IconButton>
      
      <IconButton
        onClick={handleLastPageButtonClick}
        onKeyDown={(e) => handleKeyDown(e, handleLastPageButtonClick)}
        disabled={isLastPage}
        aria-label={`Go to last page (Page ${totalPages} of ${totalPages})`}
        title={`Last page${isLastPage ? ' - Already on last page' : ''}`}
        size="small"
        sx={{ 
          backgroundColor: isLastPage ? 'transparent' : alpha(theme.palette.primary.main, 0.04),
          color: isLastPage ? theme.palette.action.disabled : theme.palette.primary.main,
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            backgroundColor: isLastPage ? 'transparent' : alpha(theme.palette.primary.main, 0.12),
            transform: isLastPage ? 'none' : 'scale(1.05)',
          },
          '&:focus': {
            outline: `2px solid ${theme.palette.primary.main}`,
            outlineOffset: 2,
          }
        }}
      >
        <LastPageIcon fontSize="small" />
      </IconButton>
    </Box>
  );
};

const DefectsPage = () => {
  const theme = useTheme();
  const [searchParams] = useSearchParams();
  
  // Authentication context
  const { user, isAuthenticated, isLoading: authLoading, canEdit, isOrganizationLevel, getUserOrganization, hasDiscipline } = useAuth();
  
  const [searchTerm, setSearchTerm] = useState('');
  const [findingIdFromUrl, setFindingIdFromUrl] = useState(null);
  const [actionSnackbarOpen, setActionSnackbarOpen] = useState(false);
  const [actionMessage, setActionMessage] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [dateFilter, setDateFilter] = useState('all');
  const [dateFilterAnchorEl, setDateFilterAnchorEl] = useState(null);
  
  // Custom date range state
  const [customDateRangeOpen, setCustomDateRangeOpen] = useState(false);
  const [customFromDate, setCustomFromDate] = useState(null);
  const [customToDate, setCustomToDate] = useState(null);

  // Filter state
  const [activeFilters, setActiveFilters] = useState({
    validationStatus: null,
    defectStatus: null,
    boundary: 'in_boundary',
    workOrder: null,
    sla: null,
  });
  // Time of day filter state
  const [timeOfDayFilter, setTimeOfDayFilter] = useState('daytime');
  const [defectTypeFilter, setDefectTypeFilter] = useState(['all']);
  const [allDefectTypes, setAllDefectTypes] = useState([]);
  const [loadingAllDefectTypes, setLoadingAllDefectTypes] = useState(false);
  
  // Repeated findings filter state
  const [repeatedFindingsPeriod, setRepeatedFindingsPeriod] = useState(null);

  // New filters state
  const [vehicleFilter, setVehicleFilter] = useState(['all']);
  const [networkFilter, setNetworkFilter] = useState(['all']);
  const [allNetworks, setAllNetworks] = useState([]);
  const [loadingAllNetworks, setLoadingAllNetworks] = useState(false);
  const [sectionFilter, setSectionFilter] = useState(['all']);
  const [allSections, setAllSections] = useState([]); // Master list of all sections
  const [filteredSections, setFilteredSections] = useState([]); // Sections filtered by selected network
  const [loadingAllSections, setLoadingAllSections] = useState(false);
  
  // User and organization state - now derived from auth context
  const [deviceCount, setDeviceCount] = useState(0);
  const [envisDevices, setEnvisDevices] = useState([]);
  
  // Defect statistics for filters
  const { stats: defectStats, refreshStats } = useDefectStats(user);

  // Table data state
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalCount, setTotalCount] = useState(0);

  // Sorting state
  const [sortField, setSortField] = useState('detectedAt');
  const [sortDirection, setSortDirection] = useState('desc');

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedDefect, setSelectedDefect] = useState(null);
  const [defectTypesList, setDefectTypesList] = useState([]);
  const [currentDefectTypeId, setCurrentDefectTypeId] = useState('');
  const [loadingDefectTypes, setLoadingDefectTypes] = useState(false);

  // SLA data state
  const [defectTypesWithSLA, setDefectTypesWithSLA] = useState(new Map());
  const [loadingSLA, setLoadingSLA] = useState(false);

  // Add Payload Modal state
  const [payloadModalOpen, setPayloadModalOpen] = useState(false);
  const [payloadText, setPayloadText] = useState('');
  const [payloadFile, setPayloadFile] = useState(null);
  const [payloadProcessing, setPayloadProcessing] = useState(false);

  // Duplication Modal state
  const [duplicationModalOpen, setDuplicationModalOpen] = useState(false);
  const [selectedRowForDuplicates, setSelectedRowForDuplicates] = useState(null);
  const [duplicateImages, setDuplicateImages] = useState([]);
  const [loadingDuplicates, setLoadingDuplicates] = useState(false);

  // Work Order Modal state
  const [workOrderModalOpen, setWorkOrderModalOpen] = useState(false);
  const [selectedDefectForWorkOrder, setSelectedDefectForWorkOrder] = useState(null);

  // Delete Confirmation Dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedRowForDelete, setSelectedRowForDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Real-time updates pause state
  const [isPaused, setIsPaused] = useState(false);

  // Use the custom hook for data management
  const {
    // getFilteredDefects and addDefect are not used in this component
    // addDefect,
  } = useDefectsData();

  // Get organization info from auth context
  const userOrganization = getUserOrganization();
  const organizationId = userOrganization?.id;
  const organizationName = userOrganization?.name || '';

  // Fetch SLA data for defect types
  const fetchDefectTypesWithSLA = useCallback(async () => {
    if (!organizationId) return;

    setLoadingSLA(true);
    try {
      const params = {
        organization_id: organizationId,
        asset_type_id: 1, // Road asset type
      };
      const response = await classificationApi.getDefectTypes(params);
      
      // Create a map of defect type names to SLA data
      const slaMap = new Map();
      response.data.forEach(defectType => {
        if (defectType.temp_fix_sla_hours || defectType.permanent_fix_sla_hours) {
          slaMap.set(defectType.name, {
            id: defectType.id,
            name: defectType.name,
            temp_fix_sla_hours: defectType.temp_fix_sla_hours,
            permanent_fix_sla_hours: defectType.permanent_fix_sla_hours
          });
        }
      });
      
      setDefectTypesWithSLA(slaMap);
      console.log('Loaded SLA data for defect types:', slaMap);
    } catch (error) {
      console.error('Failed to fetch defect types with SLA:', error);
      setError('Failed to load defect types with SLA data');
    } finally {
      setLoadingSLA(false);
    }
  }, [organizationId]);

  // Fetch defect types with SLA when organization info is available
  useEffect(() => {
    if (organizationId) {
      fetchDefectTypesWithSLA();
    }
  }, [fetchDefectTypesWithSLA]);

  // Fetch all defect types for the filter dropdown
  useEffect(() => {
    const fetchAllDefectTypes = async () => {
      if (!organizationId) return;
      setLoadingAllDefectTypes(true);
      try {
        const params = {
          organization_id: organizationId,
          asset_type_id: 1, // Road asset type
        };
        const response = await classificationApi.getDefectTypes(params);
        
        // Filter out defect types with "Aerial" in their name
        const filteredDefectTypes = (response.data || []).filter(
          type => !type.name.toLowerCase().includes('aerial')
        );
        
        setAllDefectTypes(filteredDefectTypes);
      } catch (error) {
        console.error('Failed to fetch all defect types:', error);
      } finally {
        setLoadingAllDefectTypes(false);
      }
    };

    if (organizationId) {
      fetchAllDefectTypes();
    }
  }, [organizationId]);

  // Fetch all networks for the filter dropdown
  useEffect(() => {
    const fetchAllNetworks = async () => {
      if (!organizationId) return;
      setLoadingAllNetworks(true);
      try {
        // Assuming the API returns networks scoped to the user's organization via the token
        const response = await networksApi.getAll();
        setAllNetworks(response.data || []);
      } catch (error) {
        console.error('Failed to fetch all networks:', error);
      } finally {
        setLoadingAllNetworks(false);
      }
    };

    if (organizationId) {
      fetchAllNetworks();
    }
  }, [organizationId]);

  // Fetch all sections for the filter dropdown
  useEffect(() => {
    const fetchAllSections = async () => {
      if (!organizationId) return;
      setLoadingAllSections(true);
      try {
        const response = await sectionsApi.getAll({ organization_id: organizationId });
        const sections = response.data || [];
        setAllSections(sections);
        setFilteredSections(sections); // Initially, show all sections
      } catch (error) {
        console.error('Failed to fetch all sections:', error);
        setAllSections([]);
        setFilteredSections([]);
      } finally {
        setLoadingAllSections(false);
      }
    };

    if (organizationId) {
      fetchAllSections();
    }
  }, [organizationId]);

  // Effect to filter sections based on selected networks
  useEffect(() => {
    if (networkFilter.includes('all') || networkFilter.length === 0) {
      setFilteredSections(allSections); // If no network is selected, show all sections
    } else {
      // Filter sections where section.network_id is in the networkFilter array
      const selectedNetworkIds = new Set(networkFilter.map(id => parseInt(id, 10)));
      const sectionsForSelectedNetworks = allSections.filter(section =>
        selectedNetworkIds.has(section.network_id)
      );
      setFilteredSections(sectionsForSelectedNetworks);
    }

    // Reset section filter if the new filtered list doesn't include the current selection
    const currentSectionIds = new Set(filteredSections.map(s => s.id.toString()));
    const newSelection = sectionFilter.filter(id => id === 'all' || currentSectionIds.has(id));
    if (newSelection.length === 0) {
      setSectionFilter(['all']);
    } else {
      setSectionFilter(newSelection);
    }
  }, [networkFilter, allSections]);

  // Fetch ENViS bounding boxes data
  const fetchTableData = useCallback(async (findingIdToFetch = null) => {
    if (!organizationId) {
      console.log('[DefectsPage] No organization ID available, skipping data fetch');
      return;
    }

    setLoading(true);
    setError(null);
    console.log('[DefectsPage] Fetching table data for organization:', organizationId);
    console.log(`🔐 [DefectsPage] User permission context:`, {
      username: user?.username,
      role: user?.role,
      permissionScope: user?.permissionScope,
      accessibleNetworks: user?.accessible_networks?.length || 0,
      accessibleSections: user?.accessible_sections?.length || 0
    });

    try {
      // Build query parameters based on current filters and pagination
      const params = {
        limit: rowsPerPage,
        offset: page * rowsPerPage,
        sort_by: sortField === 'detectedAt' ? 'first_detected_at' : 'id',
        sort_order: sortDirection.toUpperCase()
      };

      // Add search filter if provided
      let ambiguousNumericId = null;
      if (findingIdToFetch) {
        params.finding_id = findingIdToFetch;
      } else if (searchParams.get('defect_id')) {
        params.defect_id = searchParams.get('defect_id');
      } else if (searchTerm) {
        const trimmed = String(searchTerm).trim();
        const fMatch = /^F[-\s]?(\d+)$/i.exec(trimmed);
        const dMatch = /^D[-\s]?(\d+)$/i.exec(trimmed);
        if (fMatch) {
          params.finding_id = parseInt(fMatch[1], 10);
        } else if (dMatch) {
          params.defect_id = parseInt(dMatch[1], 10);
        } else if (/^\d+$/.test(trimmed)) {
          // Ambiguous numeric ID: try finding first, then defect
          params.finding_id = parseInt(trimmed, 10);
          ambiguousNumericId = params.finding_id;
        } else {
          params.search = trimmed;
        }
      }

      // Add active filters
      if (activeFilters.validationStatus) {
        params.validation_status = activeFilters.validationStatus;
      }
      
      if (activeFilters.defectStatus) {
        params.defect_status = activeFilters.defectStatus;
      }
      
      if (activeFilters.boundary) {
        params.boundary_status = activeFilters.boundary;
      }
      
      if (activeFilters.workOrder) {
        params.work_order_status = activeFilters.workOrder;
      }
      
      if (activeFilters.sla) {
        params.sla_status = activeFilters.sla;
      }

      // Add time of day filter
      if (timeOfDayFilter !== 'all') {
        params.time_of_day = timeOfDayFilter;
      }
      
      // Add defect type filter
      if (defectTypeFilter.length > 0 && !defectTypeFilter.includes('all')) {
        params.defect_type_ids = defectTypeFilter.join(',');
      }
      
      // Add repeated findings period filter
      console.log('🔄 [DefectsPage] repeatedFindingsPeriod state:', repeatedFindingsPeriod);
      
      if (repeatedFindingsPeriod) {
        params.has_repeated_findings = 'true';
        
        // Calculate date range based on selected period
        const now = new Date();
        let periodFromDate = null;
        
        switch (repeatedFindingsPeriod) {
          case 'today':
            periodFromDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
            break;
          case 'last7days':
            periodFromDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
            break;
          case 'last30days':
            periodFromDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
            break;
          case 'last60days':
            periodFromDate = new Date(now.getTime() - 60 * 24 * 60 * 60 * 1000);
            break;
        }
        
        if (periodFromDate) {
          params.repeated_findings_from_date = periodFromDate.toISOString();
          console.log(`🔁 [DefectsPage] Applied repeated findings filter:`, {
            period: repeatedFindingsPeriod,
            fromDate: periodFromDate.toISOString(),
            params: { has_repeated_findings: params.has_repeated_findings, repeated_findings_from_date: params.repeated_findings_from_date }
          });
        }
      }

      // Add vehicle source filter
      if (vehicleFilter.length > 0 && !vehicleFilter.includes('all')) {
        params.device_uuids = vehicleFilter.join(',');
        console.log('🚗 [DefectsPage] Applied vehicle filter:', vehicleFilter);
      }

      // Add network filter (only if no specific sections selected)
      // If sections are selected, omit network_ids to avoid backend OR-broadening
      const hasSpecificSectionSelection = sectionFilter.length > 0 && !sectionFilter.includes('all');
      if (!hasSpecificSectionSelection && networkFilter.length > 0 && !networkFilter.includes('all')) {
        const accessibleNetworkIds = user?.accessible_networks || [];
        const hasNetworkAccess = accessibleNetworkIds.length > 0;

        // If user has network-section scope, we must intersect the filter with their accessible networks
        if (user?.permissionScope === 'network_section' && hasNetworkAccess) {
            const selectedNetworks = networkFilter.filter(id => id !== 'all');
            const permittedSelection = selectedNetworks.filter(id => accessibleNetworkIds.includes(parseInt(id)));
            
            if (permittedSelection.length > 0) {
                params.network_ids = permittedSelection.join(',');
            } else {
                // If selection is outside of scope, use their default accessible networks
                params.network_ids = accessibleNetworkIds.join(',');
            }
        } else {
            // For organization-level users, just apply the filter
            params.network_ids = networkFilter.filter(id => id !== 'all').join(',');
        }
      }

      // Add section filter
      if (sectionFilter.length > 0 && !sectionFilter.includes('all')) {
        const accessibleSectionIds = user?.accessible_sections || [];
        const hasSectionAccess = accessibleSectionIds.length > 0;

        console.log(`🔍 [DefectsPage] Applying section filter:`, {
          sectionFilter,
          userPermissionScope: user?.permissionScope,
          accessibleSectionIds,
          hasSectionAccess
        });

        // If user has network-section scope, we must intersect the filter with their accessible sections
        if (user?.permissionScope === 'network_section' && hasSectionAccess) {
            const selectedSections = sectionFilter.filter(id => id !== 'all');
            const permittedSelection = selectedSections.filter(id => accessibleSectionIds.includes(parseInt(id)));
            
            if (permittedSelection.length > 0) {
                params.section_ids = permittedSelection.join(',');
                console.log(`✅ [DefectsPage] Section filter applied (network-section scope):`, params.section_ids);
            } else {
                // If selection is outside of scope, use their default accessible sections
                params.section_ids = accessibleSectionIds.join(',');
                console.log(`⚠️ [DefectsPage] Section filter overridden (outside scope):`, params.section_ids);
            }
        } else {
            // For organization-level users, just apply the filter
            params.section_ids = sectionFilter.filter(id => id !== 'all').join(',');
            console.log(`✅ [DefectsPage] Section filter applied (organization scope):`, params.section_ids);
        }
      }

      // Add date filter
      const now = new Date();
      let fromDate = null;
      let toDate = null;
      
      switch (dateFilter) {
        case 'today':
          fromDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
          break;
        case 'last7days':
          fromDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          break;
        case 'last30days':
          fromDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
          break;
        case 'thisMonth':
          fromDate = new Date(now.getFullYear(), now.getMonth(), 1);
          break;
        case 'customRange':
          if (customFromDate) {
            fromDate = customFromDate;
          }
          if (customToDate) {
            toDate = customToDate;
          }
          break;
        case 'all':
        default:
          // No date filter
          break;
      }

      if (fromDate) {
        params.from_date = fromDate.toISOString();
      }
      if (toDate) {
        params.to_date = toDate.toISOString();
      }

      // 🔐 ADD PERMISSION-BASED FILTERING
      if (user?.permissionScope === 'network_section') {
        const accessibleNetworkIds = user.accessible_networks || [];
        const accessibleSectionIds = user.accessible_sections || [];
        
        // 🔧 IMPROVED: Apply OR logic (backend handles this)
        // Include both network and section filtering - backend will use OR logic
        // 🔧 IMPROVED: Apply OR logic (backend handles this)
        // Include both network and section filtering - backend will use OR logic
        
        // Only apply default network filtering if user hasn't selected specific networks OR sections
        if (accessibleNetworkIds.length > 0 && (!params.network_ids || params.network_ids === '') && (!params.section_ids || params.section_ids === '')) {
          params.network_ids = accessibleNetworkIds.join(',');
        }
        
        // Only apply default section filtering if user hasn't selected specific sections
        if (accessibleSectionIds.length > 0 && (!params.section_ids || params.section_ids === '')) {
          params.section_ids = accessibleSectionIds.join(',');
        }
        
        // Validate user has any permissions at all
        const hasNetworkAccess = accessibleNetworkIds.length > 0;
        const hasSectionAccess = accessibleSectionIds.length > 0;
        
        if (!hasNetworkAccess && !hasSectionAccess) {
          console.warn(`[DefectsPage] User ${user.username} has no network or section permissions`);
          setTableData([]);
          setTotalCount(0);
          setError('You do not have access to any networks or sections. Please contact your administrator to assign permissions.');
          setLoading(false);
          return;
        }
        
        console.log(`🔒 [DefectsPage] Permission-based filtering applied:`, {
          finalNetworkIds: params.network_ids,
          finalSectionIds: params.section_ids,
          userAccessibleNetworks: accessibleNetworkIds.length,
          userAccessibleSections: accessibleSectionIds.length,
          userSelectedNetworks: networkFilter,
          userSelectedSections: sectionFilter
        });
      }

      // Add organization filter - always filter by user's organization
      if (organizationId && envisDevices.length > 0) {
        // Filter by devices in the organization
        // Note: API doesn't support multiple device UUIDs, so we'll fetch all and filter
      }

      console.log('📡 [DefectsPage] Making API call with params:', JSON.stringify(params, null, 2));
      let response = await envisFindingsApi.getTableFindings(params);
      // If numeric-only search produced no results as finding, retry as defect
      if (ambiguousNumericId && ((response?.data?.total ?? 0) === 0)) {
        const fallbackParams = { ...params };
        delete fallbackParams.finding_id;
        fallbackParams.defect_id = ambiguousNumericId;
        console.log('[DefectsPage] No results with finding_id, retrying as defect_id:', ambiguousNumericId);
        response = await envisFindingsApi.getTableFindings(fallbackParams);
      }
      console.log('[DefectsPage] Raw API response data:', JSON.parse(JSON.stringify(response.data.data))); // Log raw API data (deep copy for inspection)

      // Update total count from API response
      setTotalCount(response.data.total || 0);

      // Transform the data to match our table structure
      const transformedData = response.data.data.map(item => {
        const deviceName = item.device_name || item.device_uuid || 'Unknown Device';
        const vehicleId = item.vehicle_id || 'N/A';
        const deviceDisplay = `${deviceName} (${vehicleId})`;

        // Prioritize validated_defect_type_name if available, then original, then 'Unknown'
        const defectTypeName = item.validated_defect_type_name || item.defect_type_name || 'Unknown';

        // Format defect status - show only the status if defect exists
        let defectStatus = null;
        if (item.record_type === 'confirmed_defect' && item.defect_status) {
          defectStatus = item.defect_status;
        }

        // Determine validation status based on record type
        let validationStatus = item.status;
        if (item.record_type === 'confirmed_defect') {
          validationStatus = 'validated'; // All confirmed defects are validated
        }

        // Create enhanced location display with boundary status
        const networkLocation = [
          item.network_name,
          item.section_name,
          item.chainage_segment_name
        ].filter(Boolean).join(' • ');
        
        const isOutOfBoundary = !networkLocation;
        const coordinatesDisplay = `${item.latitude?.toFixed(4)}, ${item.longitude?.toFixed(4)}`;
        
        let locationDisplay;
        if (isOutOfBoundary) {
          locationDisplay = `🚫 Out of Boundary • ${coordinatesDisplay}`;
        } else {
          locationDisplay = networkLocation;
        }

        return {
          id: item.id,
          recordType: item.record_type,
          defectId: item.defect_id,
          originalBoundingBoxId: item.original_bounding_box_id,
          deviceId: deviceDisplay,
          location: locationDisplay,
          isOutOfBoundary: isOutOfBoundary,
          coordinates: coordinatesDisplay,
          defectType: defectTypeName,
          validationStatus: validationStatus,
          defectStatus: defectStatus,
          confidence: item.confidence_score,
          duplicationCount: item.duplication_count || 0,
          detectedAt: item.first_detected_at,
          createdAt: item.created_at || item.first_detected_at, // Use created_at or fall back to first_detected_at
          validatedBy: item.validated_by_name,
          validatedAt: item.validated_at,
          rejectionReason: item.rejection_reason,
          priority: item.priority,
          estimatedRepairCost: item.estimated_repair_cost,
          resolvedAt: item.resolved_at,
          resolutionNotes: item.resolution_notes,
          workOrderCount: item.work_order_count || 0,
          latestWorkOrderStatus: item.latest_work_order_status,
          hasWorkOrder: (item.work_order_count || 0) > 0,
          rawData: item
        };
      });
      console.log('[DefectsPage] Transformed data:', JSON.parse(JSON.stringify(transformedData))); // Log transformed data

      // Note: Search filtering is now handled server-side via the API
      // No need for frontend filtering since the API returns pre-filtered results
      const filteredData = transformedData;
      console.log('[DefectsPage] Server-filtered data:', JSON.parse(JSON.stringify(filteredData))); // Log server-filtered data

      // Deduplicate data based on id to prevent React key conflicts
      const deduplicatedData = filteredData.reduce((acc, current) => {
        const existingIndex = acc.findIndex(item => item.id === current.id);
        if (existingIndex === -1) {
          acc.push(current);
        } else {
          // If duplicate found, keep the one with more recent data or validated status
          if (current.status === 'validated' || current.detectedAt > acc[existingIndex].detectedAt) {
            acc[existingIndex] = current;
          }
        }
        return acc;
      }, []);
      
      console.log('[DefectsPage] Deduplicated data:', JSON.parse(JSON.stringify(deduplicatedData))); // Log deduplicated data

      setTableData(deduplicatedData);
      
      // Refresh stats after data is loaded
      refreshStats();

      // Automatically open the modal if requested by URL and the finding exists
      const openModal = searchParams.get('openModal') === 'true';
      if (openModal && findingIdToFetch) {
        const findingData = deduplicatedData.find(item => item.id.toString() === findingIdToFetch);
        if (findingData) {
          // Use a timeout to ensure the UI has rendered before opening the modal
          setTimeout(() => {
            handleStatusClick(findingData);
          }, 100);
        }
      }
    } catch (err) {
      console.error('Failed to fetch ENViS data:', err);
      setError('Failed to load defects data');
    } finally {
      setLoading(false);
    }
  }, [rowsPerPage, page, searchTerm, dateFilter, organizationId, activeFilters, user, searchParams, timeOfDayFilter, defectTypeFilter, vehicleFilter, networkFilter, sectionFilter, customFromDate, customToDate, sortField, sortDirection, repeatedFindingsPeriod]);

  // Real-time event handlers (useCallback to prevent unnecessary re-renders)
  const handleNewFinding = useCallback((notification) => {
    console.log('🔔 New finding received in frontend:', notification);
    setNewDefectData(notification.data);
    setSnackbarOpen(true);
    setRealTimeNotificationCount(prev => prev + 1);
    // Play notification sound
    const audio = new Audio('/notification.mp3');
    audio.play().catch(error => console.error("Error playing notification sound:", error));
    
    // With server-side pagination, we need to refresh the current page
    // to maintain data consistency and proper pagination
    console.log('📊 Refreshing current page due to new finding');
    fetchTableData();
    
    // Mark as newly added for visual highlighting (will be handled after refresh)
    setNewlyAddedIds(prev => new Set([...prev, notification.data.id]));
    
    // Remove highlight after 3 seconds
    setTimeout(() => {
      setNewlyAddedIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(notification.data.id);
        return newSet;
      });
    }, 3000);
  }, [fetchTableData]);

  const handleFindingUpdate = useCallback((notification) => {
    console.log('🔔 Finding updated:', notification);
    setActionMessage(`Finding ${notification.data.id} status updated`);
    setActionSnackbarOpen(true);
    
    // Refresh current page to show updated data
    fetchTableData();
  }, [fetchTableData]);

  const handleNewDefect = useCallback((notification) => {
    console.log('🔔 New defect confirmed:', notification);
    setActionMessage(`New defect confirmed: D-${notification.data.id}`);
    setActionSnackbarOpen(true);
    
    // Refresh current page to show new defect
    fetchTableData();
    
    // Mark as newly added for visual highlighting
    setNewlyAddedIds(prev => new Set([...prev, notification.data.id]));
    
    // Remove highlight after 3 seconds
    setTimeout(() => {
      setNewlyAddedIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(notification.data.id);
        return newSet;
      });
    }, 3000);
  }, [fetchTableData]);

  const handleDataRefresh = useCallback((notification) => {
    console.log('🔄 Data refresh requested:', notification);
    // Refresh current page for data refresh events
    fetchTableData();
  }, [fetchTableData]);

  // Add a fallback refresh mechanism
  const refreshTableData = useCallback(() => {
    console.log('🔄 Manual table refresh triggered');
    fetchTableData();
    refreshStats();
  }, [fetchTableData, refreshStats]);

  // Initialize real-time updates with organization and user context
  const { isConnected, connectionError, reconnect } = useRealTimeUpdates(
    // onNewFinding
    useCallback((notification) => {
      console.log('🔔 [DefectsPage] New ENViS finding notification:', notification);
      if (isPaused) return;
      // Correctly access the nested data properties for the snackbar message
      const defectType = notification.data?.defectType || 'finding';
      const deviceId = notification.data?.deviceId || 'a device';
      setActionMessage(`New ${defectType} detected by ${deviceId}`);
      setActionSnackbarOpen(true);
      refreshTableData(); // Refresh data to show new finding
    }, [refreshTableData, isPaused]),

    // onFindingUpdate
    useCallback((notification) => {
      console.log('🔔 [DefectsPage] ENViS finding update notification:', notification);
      if (isPaused) return;
      // Correctly access the nested data properties for the snackbar message
      const defectType = notification.data?.defectType || 'Finding';
      setActionMessage(`${defectType} status updated`);
      setActionSnackbarOpen(true);
      refreshTableData(); // Refresh data to show updated finding
    }, [refreshTableData, isPaused]),

    // onNewDefect
    useCallback((notification) => {
      console.log('🔔 [DefectsPage] New defect notification:', notification);
      if (isPaused) return;
      // Correctly access the nested data properties for the snackbar message
      const defectType = notification.data?.defectType || 'Defect';
      setActionMessage(`New defect validated: ${defectType}`);
      setActionSnackbarOpen(true);
      refreshTableData(); // Refresh data to show new defect
    }, [refreshTableData, isPaused]),

    // onDataRefresh
    useCallback((notification) => {
      console.log('🔄 [DefectsPage] Data refresh notification:', notification);
      if (isPaused) return;
      refreshTableData(); // Generic refresh for any data changes
    }, [refreshTableData, isPaused]),

    // onWorkOrderUpdate - NEW
    useCallback((notification) => {
      console.log('🔔 [DefectsPage] Work order update notification:', notification);
      if (isPaused) return;
      
      if (notification.type === 'work_order_status_update') {
        setActionMessage(`Action log ${notification.workOrderNumber} status changed to: ${notification.newStatus}`);
        setActionSnackbarOpen(true);
        
        // If work order completed, defect status might have changed
        if (notification.newStatus === 'completed') {
          setActionMessage(`Action log ${notification.workOrderNumber} completed - defect resolved`);
        }
        
        refreshTableData(); // Refresh to show updated defect status
      } else if (notification.type === 'work_order_completed') {
        setActionMessage(`Action log completed for defect repair`);
        setActionSnackbarOpen(true);
        refreshTableData(); // Refresh to show resolved defect
      }
    }, [refreshTableData, isPaused]),

    // onWorkOrderCreated - NEW
    useCallback((notification) => {
      console.log('🔔 [DefectsPage] Work order created notification:', notification);
      if (isPaused) return;
              setActionMessage(`Action log created for defect: ${notification.workOrder?.title}`);
      setActionSnackbarOpen(true);
      refreshTableData(); // Refresh to show work order association
    }, [refreshTableData, isPaused]),
    
    // Pass organization and user context for multi-tenant filtering
    organizationId,
    user?.id
  );

  // Fetch data when filters change or pagination changes
  useEffect(() => {
    const findingIdFromUrl = searchParams.get('finding_id');
    const defectIdFromUrl = searchParams.get('defect_id');
    // If an ID is present in the URL, the other useEffect will handle the fetch.
    // Otherwise, fetch data based on current filters.
    if (!findingIdFromUrl && !defectIdFromUrl) {
      fetchTableData();
    }
  }, [page, rowsPerPage, searchTerm, dateFilter, activeFilters, timeOfDayFilter, defectTypeFilter, vehicleFilter, networkFilter, sectionFilter, organizationId, user, customFromDate, customToDate, sortField, sortDirection, repeatedFindingsPeriod, fetchTableData]); // Rerun when any standard filter changes

  // Handle finding_id from URL on initial load
  useEffect(() => {
    const findingIdFromUrl = searchParams.get('finding_id');
    const defectIdFromUrl = searchParams.get('defect_id');

    if (findingIdFromUrl) {
      // Set the search term for display and trigger a fetch for that specific ID.
      setSearchTerm(`F-${findingIdFromUrl}`);
      fetchTableData(findingIdFromUrl);
    } else if (defectIdFromUrl) {
      // Set the search term for display and trigger a fetch for that specific ID.
      setSearchTerm(`D-${defectIdFromUrl}`);
      // The fetchTableData function will pick up the defect_id from the searchParams
      fetchTableData();
    }
    // This effect should only run once when the component mounts.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Batch processing state
  const [batchProcessing, setBatchProcessing] = useState(false);

  // Real-time updates state
  const [newDefectData, setNewDefectData] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [realTimeNotificationCount, setRealTimeNotificationCount] = useState(0);
  const [newlyAddedIds, setNewlyAddedIds] = useState(new Set());

  // Fetch user information and device count on component mount
  useEffect(() => {
    const fetchUserAndDeviceInfo = async () => {
      try {
        // Fetch devices for the user's organization
        if (organizationId) {
          const [deviceStatsResponse, devicesResponse] = await Promise.all([
            envisDevicesApi.getStats({ organization_id: organizationId }),
            envisDevicesApi.getAll({ organization_id: organizationId })
          ]);
          
          setDeviceCount(deviceStatsResponse.data.total_devices || 0);
          
          // Map the device data to ensure consistent field names
          const mappedDevices = (devicesResponse.data.devices || []).map(device => ({
            ...device,
            name: device.device_name || device.name, // Ensure 'name' field exists
            vehicle_id: device.vehicle_id || 'N/A'  // Ensure vehicle_id is never null
          }));
          
          setEnvisDevices(mappedDevices);
          console.log('📱 [DefectsPage] Loaded devices:', mappedDevices.length, mappedDevices);
        }
      } catch (error) {
        console.error('Failed to fetch user or device information:', error);
      }
    };

    fetchUserAndDeviceInfo();
  }, [organizationId]);

  const handleCloseActionSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setActionSnackbarOpen(false);
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  // Pagination handlers
  const handleChangePage = (event, newPage) => {
    const maxPage = Math.max(0, Math.ceil(totalCount / rowsPerPage) - 1);
    const validPage = Math.min(Math.max(0, newPage), maxPage);
    
    console.log(`📄 [DefectsPage] Page changed: ${page} → ${validPage} (max: ${maxPage})`);
    setPage(validPage);
    // fetchTableData will be called automatically by useEffect
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    console.log(`📄 [DefectsPage] Rows per page changed: ${rowsPerPage} → ${newRowsPerPage}`);
    setRowsPerPage(newRowsPerPage);
    setPage(0); // Reset to first page when changing rows per page
    // fetchTableData will be called automatically by useEffect
  };

  const handleDateFilterClick = (event) => {
    setDateFilterAnchorEl(event.currentTarget);
  };

  const handleDateFilterClose = () => {
    setDateFilterAnchorEl(null);
  };

  const handleDateFilterSelect = (filter) => {
    if (filter === 'customRange') {
      setCustomDateRangeOpen(true);
      setDateFilterAnchorEl(null);
    } else {
    setDateFilter(filter);
    setDateFilterAnchorEl(null);
    setPage(0); // Reset to first page when filter changes
    }
  };

  // Custom date range handlers
  const handleCustomDateRangeClose = () => {
    setCustomDateRangeOpen(false);
  };

  const handleCustomDateRangeApply = () => {
    setDateFilter('customRange');
    setCustomDateRangeOpen(false);
    setPage(0); // Reset to first page when filter changes
  };

  const handleCustomDateRangeClear = () => {
    setCustomFromDate(null);
    setCustomToDate(null);
    setDateFilter('all');
    setCustomDateRangeOpen(false);
    setPage(0);
  };

  // Handle filter changes
  const handleFiltersChange = useCallback((newFilters) => {
    setActiveFilters(newFilters);
    setPage(0); // Reset to first page when filters change
  }, []);

  // Handle repeated findings period selection
  const handleRepeatedFindingsPeriodSelect = useCallback((period) => {
    console.log('🔄 [handleRepeatedFindingsPeriodSelect] Period selected:', period);
    setRepeatedFindingsPeriod(period);
    setPage(0); // Always call setPage(0) to ensure consistency
  }, []);

  // Handle sorting
  const handleSort = (field) => {
    const isAsc = sortField === field && sortDirection === 'asc';
    setSortDirection(isAsc ? 'desc' : 'asc');
    setSortField(field);
    setPage(0); // Reset to first page when sorting changes
  };

  const getDateFilterLabel = () => {
    switch (dateFilter) {
      case 'today': return 'Today';
      case 'last7days': return 'Last 7 Days';
      case 'last30days': return 'Last 30 Days';
      case 'thisMonth': return 'This Month';
      case 'customRange': 
        if (customFromDate && customToDate) {
          return `${customFromDate.toLocaleString('en-GB', { 
            day: '2-digit', 
            month: '2-digit', 
            year: 'numeric', 
            hour: '2-digit', 
            minute: '2-digit' 
          })} - ${customToDate.toLocaleString('en-GB', { 
            day: '2-digit', 
            month: '2-digit', 
            year: 'numeric', 
            hour: '2-digit', 
            minute: '2-digit' 
          })}`;
        } else if (customFromDate) {
          return `From ${customFromDate.toLocaleString('en-GB', { 
            day: '2-digit', 
            month: '2-digit', 
            year: 'numeric', 
            hour: '2-digit', 
            minute: '2-digit' 
          })}`;
        } else if (customToDate) {
          return `Until ${customToDate.toLocaleString('en-GB', { 
            day: '2-digit', 
            month: '2-digit', 
            year: 'numeric', 
            hour: '2-digit', 
            minute: '2-digit' 
          })}`;
        }
        return 'Custom Range';
      case 'all': return 'All Time';
      default: return 'All Time';
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString();
  };

  const getStatusChip = (status, rowData) => {
    const chipProps = {
      size: 'small',
      variant: 'outlined',
      onClick: canEdit ? () => handleStatusClick(rowData) : undefined,
      sx: {
        cursor: canEdit ? 'pointer' : 'default',
        opacity: canEdit ? 1 : 0.7
      }
    };

    if (status === null || status === 'pending') {
      return <Chip label="Pending" {...chipProps} />;
    }
    
    const statusConfig = {
      draft: { label: 'Draft', color: 'default', ...chipProps },
      validated: { 
        label: 'Validated', 
        color: 'success', 
        icon: <CheckCircleIcon fontSize="inherit" />, 
        ...chipProps 
      },
      rejected: { label: 'Rejected', color: 'error', ...chipProps },
      new: { label: 'New', color: 'info', ...chipProps }, // For defect_status if needed
      in_progress: { label: 'In Progress', color: 'warning', ...chipProps }, // For defect_status
      resolved: { label: 'Resolved', color: 'secondary', ...chipProps }, // For defect_status
    };
    
    const config = statusConfig[status] || { label: status, color: 'default', ...chipProps };

    // Show validation details for validated records
    if (status === 'validated' && rowData.recordType === 'confirmed_defect') {
      // Debug console.log removed for production
      
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
          <Chip {...config} />
          {rowData.validatedBy && rowData.validatedAt && (
            <Typography variant="caption" sx={{ color: 'text.secondary', mt: 0.5, ml: 0.5 }}>
              By: {rowData.validatedBy} • {formatDateTime(rowData.validatedAt)}
            </Typography>
          )}
        </Box>
      );
    }

    // Show rejection details for rejected records
    if (status === 'rejected') {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
          <Chip {...config} />
          {rowData.validatedBy && rowData.validatedAt && (
            <Typography variant="caption" sx={{ color: 'text.secondary', mt: 0.5, ml: 0.5 }}>
              By: {rowData.validatedBy} • {formatDateTime(rowData.validatedAt)}
            </Typography>
          )}
          {rowData.rejectionReason && (
            <Typography variant="caption" sx={{ color: 'error.main', mt: 0.5, ml: 0.5, fontStyle: 'italic' }}>
              Reason: {rowData.rejectionReason}
            </Typography>
          )}
        </Box>
      );
    }

    return <Chip {...config} />;
  };

  const getDefectStatusChip = (status, rowData) => {
    // Show work order status if defect has work orders
    if (rowData.hasWorkOrder && rowData.latestWorkOrderStatus) {
      const workOrderStatusConfig = {
        pending: { label: 'WO: Pending', color: 'default', icon: '📋' },
        assigned: { label: 'WO: Assigned', color: 'info', icon: '👤' },
        in_progress: { label: 'WO: In Progress', color: 'warning', icon: '⚡' },
        completed: { label: 'WO: Completed', color: 'success', icon: '✅' },
        cancelled: { label: 'WO: Cancelled', color: 'error', icon: '❌' },
      };
      
      const workOrderConfig = workOrderStatusConfig[rowData.latestWorkOrderStatus] ||
        { label: `WO: ${rowData.latestWorkOrderStatus}`, color: 'default', icon: '📋' };
      
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Chip
            label={workOrderConfig.label}
            color={workOrderConfig.color}
            size="small"
            variant="outlined"
            sx={{
              fontWeight: 500,
              '& .MuiChip-label': {
                display: 'flex',
                alignItems: 'center',
                gap: 0.5
              }
            }}
          />
          {rowData.assignees && rowData.assignees.length > 1 && (
            <Tooltip title={rowData.assignees.map(a => a.full_name).join(', ')}>
                <Chip
                label={`+${rowData.assignees.length - 1} more`}
                size="small"
                variant="outlined"
                color="secondary"
                sx={{ fontSize: '0.7rem' }}
                />
            </Tooltip>
            )}
        </Box>
      );
    }

    if (!status) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" color="text.secondary">
            -
          </Typography>
          {/* Show work order button for confirmed defects without existing work orders */}
          {canEdit && rowData.recordType === 'confirmed_defect' && !rowData.hasWorkOrder && (
            <Tooltip title="Issue Action Log">
              <IconButton
                size="small"
                onClick={() => handleIssueWorkOrder(rowData)}
                sx={{ p: 0.5 }}
              >
                <AssignmentIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {/* Show disabled button if work order already exists */}
          {rowData.hasWorkOrder && rowData.recordType === 'confirmed_defect' && (
            <Tooltip title="Action Log Already Issued">
              <span>
                <IconButton
                  size="small"
                  disabled
                  sx={{ p: 0.5, opacity: 0.3 }}
                >
                  <AssignmentIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Box>
      );
    }

    const chipProps = {
      size: 'small',
      variant: 'outlined',
      onClick: canEdit ? () => handleDefectStatusClick(rowData) : undefined,
      sx: {
        cursor: canEdit ? 'pointer' : 'default',
        opacity: canEdit ? 1 : 0.7
      }
    };
    
    const statusConfig = {
      new: { label: 'New', color: 'info', ...chipProps },
      in_progress: { label: 'In Progress', color: 'warning', ...chipProps },
      resolved: { label: 'Resolved', color: 'success', ...chipProps },
    };
    
    const config = statusConfig[status] || { label: status, color: 'default', ...chipProps };
    
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Chip label={config.label} color={config.color} {...config} />
        {/* Show work order button for confirmed defects with 'new' status if no work order exists */}
        {canEdit && rowData.recordType === 'confirmed_defect' && (status === 'new' || !status) && !rowData.hasWorkOrder && (
          <Tooltip title="Issue Action Log">
            <IconButton
              size="small"
              onClick={() => handleIssueWorkOrder(rowData)}
              sx={{ p: 0.5 }}
            >
              <AssignmentIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {/* Show disabled button if work order already exists */}
        {rowData.hasWorkOrder && rowData.recordType === 'confirmed_defect' && (
          <Tooltip title="Action Log Already Issued">
            <span>
              <IconButton
                size="small"
                disabled
                sx={{ p: 0.5, opacity: 0.3 }}
              >
                <AssignmentIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Box>
    );
  };

  const getDuplicationChip = (rowData) => {
    const duplicationCount = rowData.duplicationCount || 0;
    
    if (duplicationCount === 0) {
      return (
        <Chip 
          label="No Duplicates" 
          size="small" 
          color="success" 
          variant="outlined"
        />
      );
    }
    
    return (
      <Chip 
        label={`${duplicationCount} Duplicate${duplicationCount > 1 ? 's' : ''}`}
        size="small" 
        color="warning" 
        variant="filled"
        onClick={() => handleDuplicationChipClick(rowData)}
        sx={{ cursor: 'pointer' }}
      />
    );
  };

  const handleDuplicationChipClick = async (rowData) => {
    setSelectedRowForDuplicates(rowData);
    setDuplicationModalOpen(true);
    setLoadingDuplicates(true);
    setDuplicateImages([]);

    try {
      // Determine the API endpoint based on record type
      let apiPath;
      let params = {};
      
      if (rowData.recordType === 'confirmed_defect') {
        // For confirmed defects, find duplicates that reference this defect
        apiPath = '/envis-findings/repeated-findings';
        params.confirmed_envis_defect_id = rowData.defectId;
      } else {
        // For pending findings, find duplicates that reference this bounding box
        apiPath = '/envis-findings/repeated-findings';
        params.original_bounding_box_id = rowData.id;
      }

      console.log('Fetching duplicates for:', rowData.recordType, 'with params:', params);

      // Use the authenticated API service instead of hardcoded URL
      const response = await api.get(apiPath, { params });
      
      console.log('Duplicate findings response:', response.data);
      setDuplicateImages(response.data.data || []);
    } catch (error) {
      console.error('Error fetching duplicate images:', error);
      setActionMessage('Error loading duplicate images');
      setActionSnackbarOpen(true);
    } finally {
      setLoadingDuplicates(false);
    }
  };

  const handleCloseDuplicationModal = () => {
    setDuplicationModalOpen(false);
    setSelectedRowForDuplicates(null);
    setDuplicateImages([]);
    setLoadingDuplicates(false);
  };

  const handleStatusClick = async (defectData) => {
    console.log('[DefectsPage] Status chip clicked:', defectData);
    
    // Check if user has edit permissions for validation actions
    if (!canEdit && defectData.validationStatus !== 'validated' && defectData.recordType !== 'confirmed_defect') {
      setActionMessage('You do not have permission to validate defects.');
      setActionSnackbarOpen(true);
      return;
    }

    // Always allow opening modal - for validated/confirmed defects it will be view-only
    setSelectedDefect(defectData);
    setModalOpen(true);

    // Only load defect types for validation (not for view-only)
    const isViewOnly = defectData.validationStatus === 'validated' || defectData.recordType === 'confirmed_defect';
    
    if (!isViewOnly && organizationId) {
      setLoadingDefectTypes(true);
      try {
        const params = {
          organization_id: organizationId,
          asset_type_id: 1, // As requested, asset_type_id is 1
        };
        
        const response = await classificationApi.getDefectTypes(params);
        setDefectTypesList(response.data || []);
        
        // Set current defect type based on row data
        const matchingType = response.data.find(type => 
          type.name.toLowerCase() === defectData.defectType?.toLowerCase()
        );
        setCurrentDefectTypeId(matchingType ? matchingType.id.toString() : '');
      } catch (error) {
        console.error('Failed to load defect types:', error);
        setActionMessage('Failed to load defect types for validation');
        setActionSnackbarOpen(true);
      } finally {
        setLoadingDefectTypes(false);
      }
    } else if (isViewOnly) {
      // For view-only mode, we don't need to load defect types but we can still set the current one
      // Set current defect type based on row data for display purposes
      setCurrentDefectTypeId(''); // Reset for view-only
      setDefectTypesList([]); // Clear list for view-only
      setLoadingDefectTypes(false);
    }
  };

  const handleDefectStatusClick = async (defectData) => {
    console.log('[DefectsPage] Defect status chip clicked:', defectData);
    
    // Check if user has edit permissions
    if (!canEdit) {
      setActionMessage('You do not have permission to update defect status.');
      setActionSnackbarOpen(true);
      return;
    }
    
    // Only allow status updates for confirmed defects
    if (defectData.recordType !== 'confirmed_defect') {
      console.log('[DefectsPage] Status update only available for confirmed defects');
      return;
    }

    // For now, just show that the status was clicked
    // In the future, this could open a status update modal
    setActionMessage(`Defect status clicked: ${defectData.defectStatus}. Status updates coming soon.`);
    setActionSnackbarOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedDefect(null);
    setDefectTypesList([]);
    setCurrentDefectTypeId('');
    setRejectionReason('');
  };

  const handleDefectTypeChange = (event) => {
    setCurrentDefectTypeId(event.target.value);
  };

  const handleValidateDefect = async (validationData = {}) => {
    if (!selectedDefect || !currentDefectTypeId) return;
    
    // Check if user has edit permissions
    if (!canEdit) {
      setActionMessage('You do not have permission to validate defects.');
      setActionSnackbarOpen(true);
      return;
    }
    
    if (!organizationId) {
      setActionMessage('Organization information not loaded. Please refresh the page.');
      setActionSnackbarOpen(true);
      return;
    }

    if (!user?.id) {
      setActionMessage('User authentication error. Please log in again.');
      setActionSnackbarOpen(true);
      return;
    }

    try {
      const updateData = {
        validated_defect_type_id: parseInt(currentDefectTypeId, 10),
        status: 'validated',
        validated_by: user?.id,
        updated_by: user?.id,
      };

      // Include adjusted bounding box coordinates if they were changed
      if (validationData.coordinatesChanged && validationData.adjustedBoundingBox) {
        const { x1, y1, x2, y2 } = validationData.adjustedBoundingBox;
        updateData.x1 = x1;
        updateData.y1 = y1;
        updateData.x2 = x2;
        updateData.y2 = y2;
        console.log('[DefectsPage] Including adjusted bounding box coordinates:', { x1, y1, x2, y2 });
      }

      // Include any additional validation data (excluding the nested structure)
      const { adjustedBoundingBox, coordinatesChanged, ...otherValidationData } = validationData;
      Object.assign(updateData, otherValidationData);

      console.log('[DefectsPage] Validating defect with data:', updateData);

      const response = await envisFindingsApi.updateBoundingBox(selectedDefect.id, updateData);
      console.log('[DefectsPage] Validation response:', response);

      if (response.status === 200) {
        // Check for auto-assign result in the response
        const autoAssignResult = response.data?.auto_assign_result;
        let successMessage = 'Defect validated successfully!';

        if (autoAssignResult?.work_order_created) {
          successMessage = `Defect validated! Work order ${autoAssignResult.work_order_number} created and assigned to: ${autoAssignResult.assigned_to_user_names}`;
        } else if (autoAssignResult && !autoAssignResult.work_order_created && autoAssignResult.message) {
          // Auto-assign was attempted but didn't create a work order
          console.log('[DefectsPage] Auto-assign result:', autoAssignResult.message);
        }

        setActionMessage(successMessage);
        setActionSnackbarOpen(true);
        setModalOpen(false);

        // Reset state
        setSelectedDefect(null);
        setCurrentDefectTypeId('');
        setRejectionReason('');

        // Refresh the table data to show updated status
        fetchTableData();
      } else {
        throw new Error('Validation failed');
      }
    } catch (error) {
      console.error('Error validating defect:', error);
      setActionMessage(`Failed to validate defect: ${error.response?.data?.error || error.message}`);
      setActionSnackbarOpen(true);
    }
  };

  const handleRejectDefect = async () => {
    if (!selectedDefect) return;
    
    // Check if user has edit permissions
    if (!canEdit) {
      setActionMessage('You do not have permission to reject defects.');
      setActionSnackbarOpen(true);
      return;
    }
    
    if (!organizationId) {
      setActionMessage('Organization information not loaded. Please refresh the page.');
      setActionSnackbarOpen(true);
      return;
    }

    if (!user?.id) {
      setActionMessage('User authentication error. Please log in again.');
      setActionSnackbarOpen(true);
      return;
    }

    try {
      const updateData = {
        status: 'rejected',
        rejection_reason: rejectionReason || 'Rejected by user',
        validated_by: user?.id,
        updated_by: user?.id,
      };
      
      console.log('[DefectsPage] Rejecting defect with data:', updateData);

      const response = await envisFindingsApi.updateBoundingBox(selectedDefect.id, updateData);
      console.log('[DefectsPage] Rejection response:', response);

      if (response.status === 200) {
        setActionMessage('Defect rejected successfully!');
        setActionSnackbarOpen(true);
        setModalOpen(false);
        
        // Reset state
        setSelectedDefect(null);
        setCurrentDefectTypeId('');
        setRejectionReason('');
        
        // Refresh the table data to show updated status
        fetchTableData();
      } else {
        throw new Error('Rejection failed');
      }
    } catch (error) {
      console.error('Error rejecting defect:', error);
      setActionMessage(`Failed to reject defect: ${error.response?.data?.error || error.message}`);
      setActionSnackbarOpen(true);
    }
  };

  // Handler for reverting a validated finding back to draft
  const handleRevertToDraft = async () => {
    if (!selectedDefect) return;

    // Check if user has manager/system_admin role
    if (!user?.role || !['manager', 'system_admin'].includes(user.role)) {
      setActionMessage('Only managers and system administrators can revert findings to draft.');
      setActionSnackbarOpen(true);
      return;
    }

    try {
      // Get the original finding ID
      // For confirmed defects, we need the original_bounding_box_id
      // For validated findings, we use the finding id directly
      const findingId = selectedDefect.recordType === 'confirmed_defect'
        ? selectedDefect.rawData?.original_bounding_box_id
        : selectedDefect.id;

      if (!findingId) {
        setActionMessage('Cannot determine finding ID. Please refresh and try again.');
        setActionSnackbarOpen(true);
        return;
      }

      console.log('[DefectsPage] Reverting finding to draft:', findingId);

      const response = await envisFindingsApi.revertToDraft(findingId);
      console.log('[DefectsPage] Revert response:', response);

      if (response.status === 200) {
        setActionMessage('Finding reverted to draft successfully!');
        setActionSnackbarOpen(true);
        setModalOpen(false);

        // Reset state
        setSelectedDefect(null);
        setCurrentDefectTypeId('');
        setRejectionReason('');

        // Refresh the table data to show updated status
        fetchTableData();
      } else {
        throw new Error('Revert failed');
      }
    } catch (error) {
      console.error('Error reverting finding to draft:', error);
      setActionMessage(`Failed to revert: ${error.response?.data?.error || error.message}`);
      setActionSnackbarOpen(true);
    }
  };

  // Add Payload Modal handlers
  const handleOpenPayloadModal = () => {
    setPayloadModalOpen(true);
  };

  const handleClosePayloadModal = () => {
    setPayloadModalOpen(false);
    setPayloadText('');
    setPayloadFile(null);
    setPayloadProcessing(false);
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file && file.type === 'application/json') {
      setPayloadFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const jsonContent = e.target.result;
          // Validate JSON
          JSON.parse(jsonContent);
          setPayloadText(jsonContent);
        } catch (error) {
          setActionMessage('Invalid JSON file');
          setActionSnackbarOpen(true);
        }
      };
      reader.readAsText(file);
    } else {
      setActionMessage('Please select a valid JSON file');
      setActionSnackbarOpen(true);
    }
  };

  const handlePayloadTextChange = (event) => {
    setPayloadText(event.target.value);
  };

  const handleSubmitPayload = async () => {
    setPayloadProcessing(true);
    try {
      // Validate JSON
      const payload = JSON.parse(payloadText);
      
      // Send payload to backend for processing using authenticated API service
      const response = await api.post('/payload/process-payload', payload);
      
      if (response.status === 200) {
        setActionMessage(`Payload processed successfully! ${response.data.message}`);
        setActionSnackbarOpen(true);
        handleClosePayloadModal();
        
        // Reset to first page and refresh the table data to show new records
        setPage(0);
        fetchTableData();
      } else {
        setActionMessage(`Failed to process payload: ${response.data.error || 'Unknown error'}`);
        setActionSnackbarOpen(true);
      }
    } catch (error) {
      console.error('Error processing payload:', error);
      if (error instanceof SyntaxError) {
        setActionMessage('Invalid JSON format');
      } else {
        setActionMessage(`Error processing payload: ${error.response?.data?.error || error.message}`);
      }
      setActionSnackbarOpen(true);
    } finally {
      setPayloadProcessing(false);
    }
  };

  // Handle batch processing
  const handleBatchProcessing = async () => {
    if (batchProcessing) {
      setActionMessage('Batch processing is already running...');
      setActionSnackbarOpen(true);
      return;
    }

    setBatchProcessing(true);
    setActionMessage('Starting batch processing of sample JSON files...');
    setActionSnackbarOpen(true);

    try {
      // Run the batch processing script using authenticated API service
      const response = await api.post('/payload/run-batch-processing');

      if (response.status === 200) {
        setActionMessage(`Batch processing completed! Processed ${response.data.totalFiles} files. Success: ${response.data.successCount}, Failed: ${response.data.errorCount}`);
        setActionSnackbarOpen(true);
        
        // Reset to first page to show newly added records
        setPage(0);
        
        // Add a fallback refresh after a short delay in case real-time updates missed anything
        setTimeout(() => {
          console.log('🔄 Fallback table refresh after batch processing');
          refreshTableData();
        }, 2000);
      } else {
        setActionMessage(`Batch processing failed: ${response.data.error || 'Unknown error'}`);
        setActionSnackbarOpen(true);
      }
    } catch (error) {
      console.error('Error running batch processing:', error);
      setActionMessage(`Error running batch processing: ${error.response?.data?.error || error.message}`);
      setActionSnackbarOpen(true);
    } finally {
      setBatchProcessing(false);
    }
  };

  // Work Order handlers
  const handleIssueWorkOrder = (defectData) => {
    console.log('[DefectsPage] Issue action log clicked for:', defectData);
    setSelectedDefectForWorkOrder(defectData);
    setWorkOrderModalOpen(true);
  };

  const handleCloseWorkOrderModal = () => {
    setWorkOrderModalOpen(false);
    setSelectedDefectForWorkOrder(null);
  };

  const handleWorkOrderCreated = () => {
    setActionMessage('Action log created successfully!');
    setActionSnackbarOpen(true);
    handleCloseWorkOrderModal();
    
    // Optionally refresh table data to show any status changes
    fetchTableData();
  };

  // Delete handlers
  const handleDeleteClick = (rowData) => {
    console.log('[DefectsPage] Delete clicked for:', rowData);
    setSelectedRowForDelete(rowData);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setSelectedRowForDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!selectedRowForDelete) return;

    setDeleting(true);
    try {
      let response;
      
      // Delete based on record type
      if (selectedRowForDelete.recordType === 'confirmed_defect') {
        // Delete defect
        response = await envisFindingsApi.deleteDefect(selectedRowForDelete.defectId);
      } else {
        // Delete bounding box (pending finding)
        response = await envisFindingsApi.deleteBoundingBox(selectedRowForDelete.id);
      }

      if (response.status === 200) {
        setActionMessage(
          `${selectedRowForDelete.recordType === 'confirmed_defect' ? 'Defect' : 'Finding'} deleted successfully!`
        );
        setActionSnackbarOpen(true);
        
        // Close dialog
        handleCloseDeleteDialog();
        
        // Refresh table data
        fetchTableData();
        
        // Refresh stats
        refreshStats();
      } else {
        throw new Error('Delete operation failed');
      }
    } catch (error) {
      console.error('Error deleting finding:', error);
      setActionMessage(
        `Failed to delete ${selectedRowForDelete.recordType === 'confirmed_defect' ? 'defect' : 'finding'}: ${
          error.response?.data?.error || error.message
        }`
      );
      setActionSnackbarOpen(true);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <DisciplineAccessControl requiredDiscipline="Road Pavement">
    <Box sx={{ padding: 3, maxWidth: '100%' }}>
      {/* Authentication Loading State */}
      {authLoading ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <CircularProgress />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Loading authentication...
          </Typography>
        </Box>
      ) :
      /* Authentication and Permission Check */
      !isAuthenticated ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h6" color="error">
            Access Denied
          </Typography>
          <Typography variant="body2" color="text.secondary">
            { "You must be logged in to access this page." }
          </Typography>
        </Box>
      ) : !organizationId ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h6" color="warning.main">
            No Organization Access
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Your account is not associated with any organization. Please contact your administrator.
          </Typography>
        </Box>
      ) : null}

      {/* Loading State */}
      {!authLoading && isAuthenticated && hasDiscipline('Road Pavement') && organizationId && loading && tableData.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <CircularProgress />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Loading defects data...
          </Typography>
        </Box>
      )}

      {/* Error State */}
      {!authLoading && isAuthenticated && hasDiscipline('Road Pavement') && organizationId && error && !loading && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h6" color="error">
            Error Loading Data
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {error}
          </Typography>
          <Button
            variant="outlined"
            onClick={() => window.location.reload()}
          >
            Retry
          </Button>
        </Box>
      )}

      {/* Main Content - Only show if authenticated, has organization, has discipline, and either has data or not loading */}
      {!authLoading && isAuthenticated && hasDiscipline('Road Pavement') && organizationId && (!loading || tableData.length > 0) && (
        <>
          {/* Header */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 600, mb: 0.5 }}>
              Defects
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
              AI-powered defect detection and validation system
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <span>
                {searchTerm || dateFilter !== 'all'
                  ? `Showing ${Math.min(rowsPerPage, tableData.length)} of ${totalCount} findings (filtered)`
                  : `Showing ${Math.min(rowsPerPage, tableData.length)} of ${totalCount} findings`}
              </span>
              {organizationName && user.role !== 'system_admin' && (
                <>
                  <span>•</span>
                  <span>{organizationName}</span>
                  <span>•</span>
                  <span>{deviceCount} ENViS devices</span>
                </>
              )}
              {user?.role === 'system_admin' && (
                <>
                  <span>•</span>
                  <span style={{ fontWeight: 500, color: theme.palette.warning.main }}>
                    System Admin View (All Organizations)
                  </span>
                </>
              )}
              {user?.permissionScope === 'network_section' && (
                <>
                  <span>•</span>
                  <span style={{ fontWeight: 500, color: theme.palette.info.main }}>
                    Limited Access ({(user.accessible_networks?.length || 0)} networks, {(user.accessible_sections?.length || 0)} sections)
                  </span>
                </>
              )}
              <span>•</span>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    backgroundColor: isConnected ? theme.palette.success.main : theme.palette.error.main,
                    animation: isConnected ? 'pulse 2s infinite' : 'none',
                    '@keyframes pulse': {
                      '0%': { opacity: 1 },
                      '50%': { opacity: 0.5 },
                      '100%': { opacity: 1 }
                    }
                  }}
                />
                <span style={{ 
                  color: isConnected ? theme.palette.success.main : theme.palette.error.main,
                  fontWeight: 500
                }}>
                  {isConnected ? (isPaused ? 'Real-time paused' : 'Real-time active') : 'Real-time disconnected'}
                  {isConnected && !isPaused && realTimeNotificationCount > 0 && ` (${realTimeNotificationCount})`}
                </span>
                {isConnected && (
                  <Tooltip title={isPaused ? 'Resume real-time updates' : 'Pause real-time updates'}>
                    <IconButton 
                      size="small" 
                      onClick={() => setIsPaused(!isPaused)} 
                      sx={{ 
                        ml: 0.5, 
                        p: 0.25,
                        minWidth: '20px',
                        width: '20px',
                        height: '20px',
                        bgcolor: isPaused 
                          ? alpha(theme.palette.success.main, 0.08) 
                          : alpha(theme.palette.warning.main, 0.08),
                        border: `1px solid ${isPaused 
                          ? alpha(theme.palette.success.main, 0.15) 
                          : alpha(theme.palette.warning.main, 0.15)}`,
                        borderRadius: '4px',
                        color: isPaused 
                          ? theme.palette.success.main 
                          : theme.palette.warning.main,
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          bgcolor: isPaused 
                            ? alpha(theme.palette.success.main, 0.15) 
                            : alpha(theme.palette.warning.main, 0.15),
                          transform: 'scale(1.1)',
                          boxShadow: `0 1px 4px ${alpha(isPaused 
                            ? theme.palette.success.main 
                            : theme.palette.warning.main, 0.25)}`,
                        },
                        '&:active': {
                          transform: 'scale(0.9)',
                        }
                      }}
                    >
                      {isPaused ? <PlayArrowIcon fontSize="inherit" /> : <PauseIcon fontSize="inherit" />}
                    </IconButton>
                  </Tooltip>
                )}
                {connectionError && !isConnected && (
                  <Tooltip title={`Connection error: ${connectionError}`}>
                    <IconButton size="small" onClick={reconnect} sx={{ ml: 0.5, p: 0.25 }}>
                      <PlayArrowIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
              {batchProcessing && (
                <>
                  <span>•</span>
                  <span style={{ color: theme.palette.warning.main, fontWeight: 500 }}>
                    Batch processing...
                  </span>
                </>
              )}
            </Typography>
          </Box>

          {/* Controls and Actions */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              mb: 3,
              flexWrap: 'wrap',
              gap: 2
            }}
          >

            {/* Search, Date Filter, and Action buttons */}
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
              {/* Search Field */}
              <TextField
                placeholder="Search by ID (F-123, D-456), devices, locations, defect types..."
                variant="outlined"
                size="small"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setPage(0); // Reset to first page when searching
                }}
                sx={{
                  width: { xs: '100%', sm: '240px' },
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    backgroundColor: 'white',
                    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.08)',
                    border: `1px solid ${alpha(theme.palette.grey[500], 0.2)}`,
                    '&:hover': {
                      bgcolor: alpha(theme.palette.primary.main, 0.04),
                      borderColor: alpha(theme.palette.primary.main, 0.3),
                    },
                    '&.Mui-focused': {
                      bgcolor: 'white',
                      borderColor: theme.palette.primary.main,
                      boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.1)}`,
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      border: 'none',
                    },
                  },
                  '& .MuiInputBase-input': {
                    fontWeight: 400,
                    fontSize: '0.875rem',
                  },
                  '& .MuiInputBase-input::placeholder': {
                    color: theme.palette.text.secondary,
                    opacity: 1,
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" sx={{ color: theme.palette.text.secondary }} />
                    </InputAdornment>
                  ),
                }}
              />

              {/* Vehicle Source Filter */}
              <FormControl
                size="small"
                sx={{
                  minWidth: 180,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    backgroundColor: 'white',
                    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.08)',
                    border: `1px solid ${alpha(theme.palette.grey[500], 0.2)}`,
                    height: '40px', // Match exact height with search field
                    '&:hover': {
                      bgcolor: alpha(theme.palette.primary.main, 0.04),
                      borderColor: alpha(theme.palette.primary.main, 0.3),
                    },
                    '&.Mui-focused': {
                      bgcolor: 'white',
                      borderColor: theme.palette.primary.main,
                      boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.1)}`,
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      border: 'none',
                    },
                  },
                  '& .MuiSelect-select': {
                    fontWeight: 400,
                    fontSize: '0.875rem',
                    display: 'flex',
                    alignItems: 'center',
                    height: '40px',
                    paddingTop: '0',
                    paddingBottom: '0',
                  },
                }}
              >
                <Select
                  multiple
                  value={vehicleFilter}
                  onChange={(e) => {
                    const value = e.target.value;
                    const newValue = typeof value === 'string' ? value.split(',') : value;
                    
                    // Handle "All" selection logic
                    if (newValue.includes('all')) {
                      // If "All" is selected, clear other selections and keep only "All"
                      if (!vehicleFilter.includes('all')) {
                        setVehicleFilter(['all']);
                      } else {
                        // If "All" was already selected and user clicked it again, deselect it
                        const withoutAll = newValue.filter(v => v !== 'all');
                        setVehicleFilter(withoutAll.length > 0 ? withoutAll : ['all']);
                      }
                    } else if (newValue.length === 0) {
                      // If no items selected, default to "All"
                      setVehicleFilter(['all']);
                    } else {
                      // Normal multi-select behavior, remove "All" if other items are selected
                      const filteredValue = newValue.filter(v => v !== 'all');
                      setVehicleFilter(filteredValue);
                    }
                    setPage(0);
                  }}
                  displayEmpty
                  renderValue={(selected) => {
                    if (selected.includes('all') || selected.length === 0) {
                      return <span style={{ color: theme.palette.text.secondary }}>All Vehicle Sources</span>;
                    }
                    if (selected.length === 1) {
                      const selectedVehicle = envisDevices.find(device => device.device_uuid === selected[0]);
                      if (selectedVehicle) {
                        const deviceName = selectedVehicle.name || selectedVehicle.device_name || 'Vehicle';
                        const vehicleId = selectedVehicle.vehicle_id || 'N/A';
                        return `${deviceName} (${vehicleId})`;
                      }
                      return 'Unknown Vehicle';
                    }
                    return `${selected.length} vehicles selected`;
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        mt: 1,
                        boxShadow: theme.shadows[3],
                        borderRadius: '8px',
                        '& .MuiMenuItem-root': {
                          py: 0.5,
                          px: 1.5,
                          minHeight: 'auto',
                          fontSize: '0.875rem',
                          '&:hover': {
                            bgcolor: alpha(theme.palette.primary.main, 0.04),
                          },
                          '&.Mui-selected': {
                            bgcolor: alpha(theme.palette.primary.main, 0.08),
                            '&:hover': {
                              bgcolor: alpha(theme.palette.primary.main, 0.12),
                            },
                          },
                        },
                        '& .MuiCheckbox-root': {
                          padding: '4px',
                          '& .MuiSvgIcon-root': {
                            fontSize: '1.1rem',
                          },
                        },
                        '& .MuiListItemText-root': {
                          margin: 0,
                          '& .MuiListItemText-primary': {
                            fontSize: '0.875rem',
                            lineHeight: 1.4,
                          },
                        },
                      },
                    },
                  }}
                >
                  <MenuItem value="all">
                    <Checkbox checked={vehicleFilter.includes('all')} />
                    <ListItemText primary="All Vehicle Sources" />
                  </MenuItem>
                  {envisDevices.map((device) => {
                    const deviceName = device.name || device.device_name || 'Vehicle';
                    const vehicleId = device.vehicle_id || 'N/A';
                    return (
                      <MenuItem key={device.device_uuid} value={device.device_uuid}>
                        <Checkbox checked={vehicleFilter.indexOf(device.device_uuid) > -1} />
                        <ListItemText primary={`${deviceName} (${vehicleId})`} />
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>

              {/* Network Filter */}
              <FormControl
                size="small"
                sx={{
                  minWidth: 180,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    backgroundColor: 'white',
                    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.08)',
                    border: `1px solid ${alpha(theme.palette.grey[500], 0.2)}`,
                    height: '40px', // Match exact height with search field
                    '&:hover': {
                      bgcolor: alpha(theme.palette.primary.main, 0.04),
                      borderColor: alpha(theme.palette.primary.main, 0.3),
                    },
                    '&.Mui-focused': {
                      bgcolor: 'white',
                      borderColor: theme.palette.primary.main,
                      boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.1)}`,
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      border: 'none',
                    },
                  },
                  '& .MuiSelect-select': {
                    fontWeight: 400,
                    fontSize: '0.875rem',
                    display: 'flex',
                    alignItems: 'center',
                    height: '40px',
                    paddingTop: '0',
                    paddingBottom: '0',
                  },
                }}
              >
                <Select
                  multiple
                  value={networkFilter}
                  onChange={(e) => {
                    const value = e.target.value;
                    const newValue = typeof value === 'string' ? value.split(',') : value;
                    
                    // Handle "All" selection logic
                    if (newValue.includes('all')) {
                      // If "All" is selected, clear other selections and keep only "All"
                      if (!networkFilter.includes('all')) {
                        setNetworkFilter(['all']);
                      } else {
                        // If "All" was already selected and user clicked it again, deselect it
                        const withoutAll = newValue.filter(v => v !== 'all');
                        setNetworkFilter(withoutAll.length > 0 ? withoutAll : ['all']);
                      }
                    } else if (newValue.length === 0) {
                      // If no items selected, default to "All"
                      setNetworkFilter(['all']);
                    } else {
                      // Normal multi-select behavior, remove "All" if other items are selected
                      const filteredValue = newValue.filter(v => v !== 'all');
                      setNetworkFilter(filteredValue);
                    }
                    setPage(0);
                  }}
                  disabled={loadingAllNetworks}
                  displayEmpty
                  renderValue={(selected) => {
                    if (selected.includes('all') || selected.length === 0) {
                      return <span style={{ color: theme.palette.text.secondary }}>All Networks</span>;
                    }
                    if (selected.length === 1) {
                      const selectedNetwork = allNetworks.find(network => network.id.toString() === selected[0].toString());
                      return selectedNetwork ? selectedNetwork.name : 'All Networks';
                    }
                    return <span style={{ color: theme.palette.text.primary }}>{selected.length} networks selected</span>;
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        mt: 1,
                        boxShadow: theme.shadows[3],
                        borderRadius: '8px',
                        '& .MuiMenuItem-root': {
                          py: 0.5,
                          px: 1.5,
                          minHeight: 'auto',
                          fontSize: '0.875rem',
                          '&:hover': {
                            bgcolor: alpha(theme.palette.primary.main, 0.04),
                          },
                          '&.Mui-selected': {
                            bgcolor: alpha(theme.palette.primary.main, 0.08),
                            '&:hover': {
                              bgcolor: alpha(theme.palette.primary.main, 0.12),
                            },
                          },
                        },
                        '& .MuiCheckbox-root': {
                          padding: '4px',
                          '& .MuiSvgIcon-root': {
                            fontSize: '1.1rem',
                          },
                        },
                        '& .MuiListItemText-root': {
                          margin: 0,
                          '& .MuiListItemText-primary': {
                            fontSize: '0.875rem',
                            lineHeight: 1.4,
                          },
                        },
                      },
                    },
                  }}
                >
                  <MenuItem value="all">
                    <Checkbox checked={networkFilter.includes('all')} sx={{ mr: 0.5 }} />
                    <ListItemText primary="All Networks" sx={{ '& .MuiListItemText-primary': { fontWeight: networkFilter.includes('all') ? 500 : 400 } }} />
                  </MenuItem>
                  {allNetworks.map((network) => (
                    <MenuItem key={network.id} value={network.id}>
                      <Checkbox checked={networkFilter.includes(network.id.toString()) || networkFilter.includes(network.id)} sx={{ mr: 0.5 }} />
                      <ListItemText primary={network.name} sx={{ '& .MuiListItemText-primary': { fontWeight: (networkFilter.includes(network.id.toString()) || networkFilter.includes(network.id)) ? 500 : 400 } }} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Section Filter */}
              <FormControl
                size="small"
                sx={{
                  minWidth: 180,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    backgroundColor: 'white',
                    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.08)',
                    border: `1px solid ${alpha(theme.palette.grey[500], 0.2)}`,
                    height: '40px', // Match exact height with search field
                    '&:hover': {
                      bgcolor: alpha(theme.palette.primary.main, 0.04),
                      borderColor: alpha(theme.palette.primary.main, 0.3),
                    },
                    '&.Mui-focused': {
                      bgcolor: 'white',
                      borderColor: theme.palette.primary.main,
                      boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.1)}`,
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      border: 'none',
                    },
                  },
                  '& .MuiSelect-select': {
                    fontWeight: 400,
                    fontSize: '0.875rem',
                    display: 'flex',
                    alignItems: 'center',
                    height: '40px',
                    paddingTop: '0',
                    paddingBottom: '0',
                  },
                }}
              >
                <Select
                  multiple
                  value={sectionFilter}
                  onChange={(e) => {
                    const value = e.target.value;
                    const newValue = typeof value === 'string' ? value.split(',') : value;
                    
                    // Handle "All" selection logic
                    if (newValue.includes('all')) {
                      // If "All" is selected, clear other selections and keep only "All"
                      if (!sectionFilter.includes('all')) {
                        setSectionFilter(['all']);
                      } else {
                        // If "All" was already selected and user clicked it again, deselect it
                        const withoutAll = newValue.filter(v => v !== 'all');
                        setSectionFilter(withoutAll.length > 0 ? withoutAll : ['all']);
                      }
                    } else if (newValue.length === 0) {
                      // If no items selected, default to "All"
                      setSectionFilter(['all']);
                    } else {
                      // Normal multi-select behavior, remove "All" if other items are selected
                      const filteredValue = newValue.filter(v => v !== 'all');
                      setSectionFilter(filteredValue);
                    }
                    setPage(0);
                  }}
                  disabled={loadingAllSections}
                  displayEmpty
                  renderValue={(selected) => {
                    if (selected.includes('all') || selected.length === 0) {
                      return <span style={{ color: theme.palette.text.secondary }}>All Sections</span>;
                    }
                    if (selected.length === 1) {
                      const selectedSection = filteredSections.find(section => section.id.toString() === selected[0].toString());
                      return selectedSection ? selectedSection.name : 'All Sections';
                    }
                    return <span style={{ color: theme.palette.text.primary }}>{selected.length} sections selected</span>;
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        mt: 1,
                        boxShadow: theme.shadows[3],
                        borderRadius: '8px',
                        '& .MuiMenuItem-root': {
                          py: 0.5,
                          px: 1.5,
                          minHeight: 'auto',
                          fontSize: '0.875rem',
                          '&:hover': {
                            bgcolor: alpha(theme.palette.primary.main, 0.04),
                          },
                          '&.Mui-selected': {
                            bgcolor: alpha(theme.palette.primary.main, 0.08),
                            '&:hover': {
                              bgcolor: alpha(theme.palette.primary.main, 0.12),
                            },
                          },
                        },
                        '& .MuiCheckbox-root': {
                          padding: '4px',
                          '& .MuiSvgIcon-root': {
                            fontSize: '1.1rem',
                          },
                        },
                        '& .MuiListItemText-root': {
                          margin: 0,
                          '& .MuiListItemText-primary': {
                            fontSize: '0.875rem',
                            lineHeight: 1.4,
                          },
                        },
                      },
                    },
                  }}
                >
                  <MenuItem value="all">
                    <Checkbox checked={sectionFilter.includes('all')} sx={{ mr: 0.5 }} />
                    <ListItemText primary="All Sections" sx={{ '& .MuiListItemText-primary': { fontWeight: sectionFilter.includes('all') ? 500 : 400 } }} />
                  </MenuItem>
                  {loadingAllSections ? (
                    <MenuItem disabled>
                      <CircularProgress size={20} sx={{ mr: 1 }} />
                      <ListItemText primary="Loading sections..." />
                    </MenuItem>
                  ) : filteredSections.length > 0 ? (
                    filteredSections.map((section) => (
                      <MenuItem key={section.id} value={section.id}>
                        <Checkbox checked={sectionFilter.includes(section.id.toString()) || sectionFilter.includes(section.id)} sx={{ mr: 0.5 }} />
                        <ListItemText
                          primary={section.name}
                          secondary={`Network: ${allNetworks.find(n => n.id === section.network_id)?.name || 'Unknown'}`}
                          sx={{
                            '& .MuiListItemText-primary': {
                              fontWeight: (sectionFilter.includes(section.id.toString()) || sectionFilter.includes(section.id)) ? 500 : 400
                            },
                            '& .MuiListItemText-secondary': {
                              fontSize: '0.75rem',
                              fontStyle: 'italic'
                            }
                          }}
                        />
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled>
                      <ListItemText primary="No sections available for selected network(s)" />
                    </MenuItem>
                  )}
                </Select>
              </FormControl>

              {/* Defect Type Filter */}
              <FormControl
                size="small"
                sx={{
                  minWidth: 180,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    backgroundColor: 'white',
                    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.08)',
                    border: `1px solid ${alpha(theme.palette.grey[500], 0.2)}`,
                    height: '40px', // Match exact height with search field
                    '&:hover': {
                      bgcolor: alpha(theme.palette.primary.main, 0.04),
                      borderColor: alpha(theme.palette.primary.main, 0.3),
                    },
                    '&.Mui-focused': {
                      bgcolor: 'white',
                      borderColor: theme.palette.primary.main,
                      boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.1)}`,
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      border: 'none',
                    },
                  },
                  '& .MuiSelect-select': {
                    fontWeight: 400,
                    fontSize: '0.875rem',
                    display: 'flex',
                    alignItems: 'center',
                    height: '40px',
                    paddingTop: '0',
                    paddingBottom: '0',
                  },
                }}
              >
                <Select
                  multiple
                  value={defectTypeFilter}
                  onChange={(e) => {
                    const value = e.target.value;
                    const newValue = typeof value === 'string' ? value.split(',') : value;
                    
                    // Handle "All" selection logic
                    if (newValue.includes('all')) {
                      // If "All" is selected, clear other selections and keep only "All"
                      if (!defectTypeFilter.includes('all')) {
                        setDefectTypeFilter(['all']);
                      } else {
                        // If "All" was already selected and user clicked it again, deselect it
                        const withoutAll = newValue.filter(v => v !== 'all');
                        setDefectTypeFilter(withoutAll.length > 0 ? withoutAll : ['all']);
                      }
                    } else if (newValue.length === 0) {
                      // If no items selected, default to "All"
                      setDefectTypeFilter(['all']);
                    } else {
                      // Normal multi-select behavior, remove "All" if other items are selected
                      const filteredValue = newValue.filter(v => v !== 'all');
                      setDefectTypeFilter(filteredValue);
                    }
                    setPage(0);
                  }}
                  disabled={loadingAllDefectTypes}
                  displayEmpty
                  renderValue={(selected) => {
                    if (selected.includes('all') || selected.length === 0) {
                      return <span style={{ color: theme.palette.text.secondary }}>All Defect Types</span>;
                    }
                    if (selected.length === 1) {
                      const selectedType = allDefectTypes.find(type => type.id.toString() === selected[0].toString());
                    return selectedType ? selectedType.name : 'All Defect Types';
                    }
                    return <span style={{ color: theme.palette.text.primary }}>{selected.length} defect types selected</span>;
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        mt: 1,
                        boxShadow: theme.shadows[3],
                        borderRadius: '8px',
                        '& .MuiMenuItem-root': {
                          py: 0.5, // Reduced padding from 1 to 0.5
                          px: 1.5,
                          minHeight: 'auto', // Remove minimum height constraint
                          fontSize: '0.875rem', // Smaller font size
                          '&:hover': {
                            bgcolor: alpha(theme.palette.primary.main, 0.04),
                          },
                          '&.Mui-selected': {
                            bgcolor: alpha(theme.palette.primary.main, 0.08),
                            '&:hover': {
                              bgcolor: alpha(theme.palette.primary.main, 0.12),
                            },
                          },
                        },
                        '& .MuiCheckbox-root': {
                          padding: '4px', // Compact checkbox padding
                          '& .MuiSvgIcon-root': {
                            fontSize: '1.1rem', // Smaller checkbox icon
                          },
                        },
                        '& .MuiListItemText-root': {
                          margin: 0, // Remove default margins
                          '& .MuiListItemText-primary': {
                            fontSize: '0.875rem', // Consistent font size
                            lineHeight: 1.4, // Tighter line height
                          },
                        },
                      },
                    },
                  }}
                >
                  <MenuItem value="all">
                    <Checkbox
                      checked={defectTypeFilter.includes('all')}
                      sx={{ mr: 0.5 }} // Reduced margin
                    />
                    <ListItemText
                      primary="All Defect Types"
                      sx={{
                        '& .MuiListItemText-primary': {
                          fontWeight: defectTypeFilter.includes('all') ? 500 : 400
                        }
                      }}
                    />
                  </MenuItem>
                  {allDefectTypes.map((type) => (
                    <MenuItem key={type.id} value={type.id}>
                      <Checkbox
                        checked={defectTypeFilter.includes(type.id.toString()) || defectTypeFilter.includes(type.id)}
                        sx={{ mr: 0.5 }} // Reduced margin
                      />
                      <ListItemText
                        primary={type.name}
                        sx={{
                          '& .MuiListItemText-primary': {
                            fontWeight: (defectTypeFilter.includes(type.id.toString()) || defectTypeFilter.includes(type.id)) ? 500 : 400
                          }
                        }}
                      />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              {/* Date Filter Button */}
              <Button
                variant="outlined"
                size="small"
                startIcon={<DateRangeIcon />}
                onClick={handleDateFilterClick}
                sx={{
                  borderRadius: '8px',
                  border: `1px solid ${alpha(theme.palette.grey[500], 0.2)}`,
                  boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.08)',
                  bgcolor: 'white',
                  '&:hover': {
                    bgcolor: alpha(theme.palette.primary.main, 0.04),
                  }
                }}
              >
                {getDateFilterLabel()}
              </Button>
              
              {/* Date Filter Menu */}
              <Menu
                anchorEl={dateFilterAnchorEl}
                open={Boolean(dateFilterAnchorEl)}
                onClose={handleDateFilterClose}
                PaperProps={{
                  sx: {
                    mt: 1.5,
                    boxShadow: theme.shadows[3],
                    borderRadius: '8px',
                    width: 200,
                  }
                }}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
              >
                <MenuItem onClick={() => handleDateFilterSelect('today')} selected={dateFilter === 'today'} sx={{ py: 1 }}>
                  <ListItemIcon><TodayIcon fontSize="small" color={dateFilter === 'today' ? 'primary' : 'inherit'} /></ListItemIcon>
                  <ListItemText>Today</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => handleDateFilterSelect('last7days')} selected={dateFilter === 'last7days'} sx={{ py: 1 }}>
                  <ListItemIcon><EventNoteIcon fontSize="small" color={dateFilter === 'last7days' ? 'primary' : 'inherit'} /></ListItemIcon>
                  <ListItemText>Last 7 Days</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => handleDateFilterSelect('last30days')} selected={dateFilter === 'last30days'} sx={{ py: 1 }}>
                  <ListItemIcon><DateRangeIcon fontSize="small" color={dateFilter === 'last30days' ? 'primary' : 'inherit'} /></ListItemIcon>
                  <ListItemText>Last 30 Days</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => handleDateFilterSelect('thisMonth')} selected={dateFilter === 'thisMonth'} sx={{ py: 1 }}>
                  <ListItemIcon><CalendarMonthIcon fontSize="small" color={dateFilter === 'thisMonth' ? 'primary' : 'inherit'} /></ListItemIcon>
                  <ListItemText>This Month</ListItemText>
                </MenuItem>
                <Divider />
                <MenuItem onClick={() => handleDateFilterSelect('customRange')} selected={dateFilter === 'customRange'} sx={{ py: 1 }}>
                  <ListItemIcon><DateRangeIcon fontSize="small" color={dateFilter === 'customRange' ? 'primary' : 'inherit'} /></ListItemIcon>
                  <ListItemText>Custom Range</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => handleDateFilterSelect('all')} selected={dateFilter === 'all'} sx={{ py: 1 }}>
                  <ListItemIcon><HistoryIcon fontSize="small" color={dateFilter === 'all' ? 'primary' : 'inherit'} /></ListItemIcon>
                  <ListItemText>All Time</ListItemText>
                </MenuItem>
              </Menu>

              <ToggleButtonGroup
                value={timeOfDayFilter}
                exclusive
                onChange={(e, newValue) => {
                  if (newValue !== null) {
                    setTimeOfDayFilter(newValue);
                  }
                }}
                size="small"
                aria-label="Time of day filter"
                sx={{
                  height: '30px', // Match button height exactly
                  borderRadius: '8px',
                  boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.08)',
                  bgcolor: 'white',
                  border: `1px solid ${alpha(theme.palette.grey[500], 0.2)}`,
                  '& .MuiToggleButton-root': {
                    border: 'none',
                    borderRadius: '0',
                    color: theme.palette.text.primary,
                    px: 1.5,
                    py: 0,
                    minWidth: '36px',
                    width: '36px',
                    height: '28px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      bgcolor: alpha(theme.palette.primary.main, 0.04),
                      color: theme.palette.primary.main,
                    },
                    '&.Mui-selected': {
                      bgcolor: alpha(theme.palette.primary.main, 0.08),
                      color: theme.palette.primary.main,
                      '&:hover': {
                        bgcolor: alpha(theme.palette.primary.main, 0.12),
                      },
                    },
                    '&:first-of-type': {
                      borderRadius: '7px 0 0 7px',
                    },
                    '&:last-of-type': {
                      borderRadius: '0 7px 7px 0',
                    },
                    '&:not(:last-of-type)': {
                      borderRight: `1px solid ${alpha(theme.palette.grey[500], 0.15)}`,
                    },
                  },
                }}
              >
                <Tooltip title="Daytime">
                  <ToggleButton value="daytime" aria-label="daytime">
                    <WbSunnyIcon sx={{ fontSize: '16px' }} />
                  </ToggleButton>
                </Tooltip>
                <Tooltip title="Nighttime">
                  <ToggleButton value="nighttime" aria-label="nighttime">
                    <NightsStayIcon sx={{ fontSize: '16px' }} />
                  </ToggleButton>
                </Tooltip>
                <Tooltip title="All-Day">
                  <ToggleButton value="all" aria-label="all day">
                    <WatchLaterIcon sx={{ fontSize: '16px' }} />
                  </ToggleButton>
                </Tooltip>
              </ToggleButtonGroup>

              {/* Action Buttons - Only show to users who can edit */}
              {canEdit && (
                <>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={handleOpenPayloadModal}
                    sx={{
                      display: 'none',
                      borderRadius: '8px',
                      border: `1px solid ${alpha(theme.palette.grey[500], 0.2)}`,
                      boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.08)',
                      bgcolor: 'white',
                      '&:hover': {
                        bgcolor: alpha(theme.palette.primary.main, 0.04),
                      }
                    }}
                  >
                    Add Payload
                  </Button>
                  
                  <Tooltip title={batchProcessing ? "Batch processing in progress..." : "Run batch processing of sample JSON files"}>
                    <IconButton
                      size="small"
                      onClick={handleBatchProcessing}
                      color={batchProcessing ? "warning" : "primary"}
                      disabled={batchProcessing}
                      sx={{
                        display: 'none',
                        bgcolor: alpha(batchProcessing ? theme.palette.warning.main : theme.palette.primary.main, 0.1),
                        '&:hover': {
                          bgcolor: alpha(batchProcessing ? theme.palette.warning.main : theme.palette.primary.main, 0.2),
                        },
                        animation: batchProcessing ? 'pulse 2s infinite' : 'none',
                        '@keyframes pulse': {
                          '0%': { boxShadow: `0 0 0 0 ${alpha(theme.palette.warning.main, 0.4)}` },
                          '70%': { boxShadow: `0 0 0 8px ${alpha(theme.palette.warning.main, 0)}` },
                        }
                      }}
                    >
                      {batchProcessing ? <CircularProgress size={20} color="inherit" /> : <PlayArrowIcon />}
                    </IconButton>
                  </Tooltip>
                </>
              )}
            </Box>
          </Box>

          {/* Table Findings */}
          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 500 }}>
              Table Findings
            </Typography>
            
            {/* Defects Filters */}
            <DefectsFilters
              onFiltersChange={handleFiltersChange}
              defectStats={defectStats}
              initialFilters={activeFilters}
            />
            
            {/* Repeated Findings Summary Panel */}
            <RepeatedFindingsSummary
              onPeriodSelect={handleRepeatedFindingsPeriodSelect}
              selectedPeriod={repeatedFindingsPeriod}
              boundaryStatus={activeFilters.boundary}
              timeOfDay={timeOfDayFilter}
            />
            
            {loading && (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', my: 4 }}>
                <CircularProgress size={40} thickness={4} />
                <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
                  Loading defects data, please wait...
                </Typography>
              </Box>
            )}
            {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
            {!loading && !error && (
              <TableContainer 
                component={Paper} 
                sx={{ 
                  borderRadius: '12px',
                  boxShadow: theme.shadows[2],
                  overflow: 'hidden',
                  mb: 3
                }}
              >
                <Table sx={{ minWidth: 650 }} aria-label="defects table">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.04) }}>
                      <TableCell sx={{ fontWeight: 'bold' }}>
                        <TableSortLabel
                          active={sortField === 'id'}
                          direction={sortField === 'id' ? sortDirection : 'asc'}
                          onClick={() => handleSort('id')}
                          sx={{ fontWeight: 'bold' }}
                        >
                          ID
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Source</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Defect Type</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Validation Status</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Defect Status</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Duplication</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', width: '280px', minWidth: '280px' }}>
                        <TableSortLabel
                          active={sortField === 'detectedAt'}
                          direction={sortField === 'detectedAt' ? sortDirection : 'asc'}
                          onClick={() => handleSort('detectedAt')}
                          sx={{ fontWeight: 'bold' }}
                        >
                          Time Progress
                        </TableSortLabel>
                      </TableCell>
                      {user?.role === 'manager' && (
                        <TableCell sx={{ fontWeight: 'bold', width: '80px' }}>Actions</TableCell>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {tableData.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={user?.role === 'manager' ? 8 : 7} align="center">
                          {loading ? 'Loading...' : 'No defects found.'}
                        </TableCell>
                      </TableRow>
                    ) : (
                      tableData.map((row, index) => (
                        <TableRow
                          key={`${row.recordType}-${row.id}-${index}`}
                          sx={{ 
                            '&:last-child td, &:last-child th': { border: 0 },
                            cursor: 'pointer',
                            '&:hover': {
                              backgroundColor: alpha(theme.palette.primary.main, 0.04),
                            },
                            // Highlight newly added rows with animation
                            bgcolor: newlyAddedIds.has(row.id)
                              ? alpha(theme.palette.warning.main, 0.15)
                              : row.recordType === 'confirmed_defect' 
                                ? alpha(theme.palette.success.main, 0.02) 
                                : 'inherit',
                            transition: 'background-color 0.3s ease-in-out',
                            // Add subtle border for newly added items
                            borderLeft: newlyAddedIds.has(row.id) 
                              ? `3px solid ${theme.palette.warning.main}` 
                              : 'none',
                            animation: newlyAddedIds.has(row.id) 
                              ? 'slideInFromTop 0.5s ease-out' 
                              : 'none',
                            '@keyframes slideInFromTop': {
                              '0%': {
                                transform: 'translateY(-20px)',
                                opacity: 0
                              },
                              '100%': {
                                transform: 'translateY(0)',
                                opacity: 1
                              }
                            }
                          }}
                          hover
                        >
                        {/* Combined ID Column with Record Type styling */}
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Chip 
                              label={row.recordType === 'confirmed_defect' ? `D-${row.defectId}` : `F-${row.id}`}
                              icon={row.recordType === 'confirmed_defect' ? 
                                <BugReportIcon fontSize="small" /> : 
                                <HelpIcon fontSize="small" />
                              }
                              size="small"
                              sx={{
                                bgcolor: row.recordType === 'confirmed_defect' 
                                  ? alpha(theme.palette.success.main, 0.1)
                                  : alpha(theme.palette.info.main, 0.1),
                                color: row.recordType === 'confirmed_defect' 
                                  ? theme.palette.success.main
                                  : theme.palette.info.main,
                                border: `1px solid ${row.recordType === 'confirmed_defect' 
                                  ? alpha(theme.palette.success.main, 0.3)
                                  : alpha(theme.palette.info.main, 0.3)}`,
                                fontWeight: 600,
                                fontSize: '0.75rem',
                                '& .MuiChip-icon': {
                                  color: row.recordType === 'confirmed_defect' 
                                    ? theme.palette.success.main
                                    : theme.palette.info.main,
                                  fontSize: '0.875rem'
                                }
                              }}
                            />
                          </Box>
                        </TableCell>
                        {/* Combined Source Column (Device + Location) */}
                        <TableCell>
                          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.2 }}>
                              {row.deviceId}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.25 }}>
                              {row.isOutOfBoundary ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                  <Chip 
                                    label="Out of Boundary" 
                                    size="small" 
                                    color="warning" 
                                    variant="outlined"
                                    sx={{ 
                                      fontSize: '0.7rem',
                                      height: '20px',
                                      '& .MuiChip-label': { 
                                        px: 1,
                                        fontWeight: 500
                                      }
                                    }}
                                  />
                                  <Typography 
                                    variant="caption" 
                                    sx={{ 
                                      color: 'text.secondary', 
                                      lineHeight: 1.2,
                                      fontSize: '0.75rem'
                                    }}
                                  >
                                    📍 {row.coordinates}
                                  </Typography>
                                </Box>
                              ) : (
                                <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.2 }}>
                                  📍 {row.location}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.2 }}>
                              {row.defectType}
                            </Typography>
                            <Typography 
                              variant="caption" 
                              sx={{ 
                                color: 'text.secondary', 
                                lineHeight: 1.2,
                                fontSize: '0.75rem',
                                mt: 0.25
                              }}
                            >
                              at {new Date(row.detectedAt).toLocaleTimeString('en-US', {
                                hour: 'numeric',
                                minute: '2-digit',
                                hour12: true
                              })} ({new Date(row.detectedAt).toLocaleDateString('en-GB', {
                                day: 'numeric',
                                month: 'numeric',
                                year: 'numeric'
                              })})
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          {getStatusChip(row.validationStatus, row)}
                        </TableCell>
                        <TableCell>
                          {getDefectStatusChip(row.defectStatus, row)}
                        </TableCell>
                        <TableCell>
                          {getDuplicationChip(row)}
                        </TableCell>
                        <TableCell sx={{ width: '280px', minWidth: '280px' }}>
                          <TimeProgressBar
                            createdAt={row.createdAt}
                            defectTypeSLA={defectTypesWithSLA.get(row.defectType)}
                            status={row.defectStatus}
                            defectType={row.defectType}
                            validationStatus={row.validationStatus}
                            resolvedAt={row.resolvedAt}
                            workOrderStatus={row.latestWorkOrderStatus}
                            hasWorkOrder={row.hasWorkOrder}
                          />
                        </TableCell>
                        {/* Actions Column - Only visible to managers */}
                        {user?.role === 'manager' && (
                          <TableCell sx={{ width: '80px' }}>
                            <Tooltip title="Delete Finding">
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation(); // Prevent row click
                                  handleDeleteClick(row);
                                }}
                                sx={{ 
                                  color: theme.palette.error.main,
                                  '&:hover': {
                                    backgroundColor: alpha(theme.palette.error.main, 0.1),
                                  }
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        )}
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
                <TablePagination 
                  rowsPerPageOptions={[10, 25, 50, 100, 250, 500]}
                  component="div"
                  count={totalCount}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  ActionsComponent={TablePaginationActions}
                  sx={{
                    borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                    '.MuiTablePagination-toolbar': {
                      paddingRight: 3,
                    },
                    '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                      fontWeight: 500,
                    },
                    '.MuiTablePagination-select': {
                      fontWeight: 500,
                    },
                    '.MuiTablePagination-displayedRows': {
                      minWidth: '120px',
                    },
                    '.MuiTablePagination-actions': {
                      marginLeft: 2,
                    }
                  }}
                  labelRowsPerPage="Show rows:"
                  labelDisplayedRows={({ from, to, count }) => {
                    const currentPage = page + 1;
                    const totalPages = Math.ceil(count / rowsPerPage);
                    return (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <span>{`${from}-${to} of ${count !== -1 ? count : `more than ${to}`} defects`}</span>
                        <span style={{ 
                          color: theme.palette.text.secondary, 
                          fontSize: '0.875rem',
                          fontWeight: 400
                        }}>
                          • Page {currentPage} of {totalPages}
                        </span>
                      </Box>
                    );
                  }}
                />
              </TableContainer>
            )}
          </Box>

          {/* Real-time New Finding Snackbar */}
          <Snackbar
            open={snackbarOpen}
            autoHideDuration={6000}
            onClose={handleCloseSnackbar}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          >
            <Alert
              onClose={handleCloseSnackbar}
              severity={newDefectData?.priority === 'high' ? 'warning' : 'info'}
              icon={
                <NotificationsActiveIcon sx={{
                  fontSize: '1.5rem',
                  animation: 'bellRing 1s infinite ease-in-out',
                  transformOrigin: '50% 0',
                  '@keyframes bellRing': {
                    '0%': { transform: 'rotate(0)' },
                    '10%': { transform: 'rotate(15deg)' },
                    '20%': { transform: 'rotate(-10deg)' },
                    '30%': { transform: 'rotate(8deg)' },
                    '40%': { transform: 'rotate(-5deg)' },
                    '50%': { transform: 'rotate(0)' },
                    '100%': { transform: 'rotate(0)' }
                  }
                }} />
              }
              sx={{
                width: '100%',
                boxShadow: theme.shadows[3],
                backgroundColor: newDefectData?.priority === 'high' 
                  ? alpha(theme.palette.warning.main, 0.95) 
                  : alpha(theme.palette.info.main, 0.95),
                color: 'white',
                '& .MuiAlert-icon': {
                  color: 'white'
                }
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  🔔 New {newDefectData?.defectType || 'Defect'} Detected!
                </Typography>
                {newDefectData && (
                  <>
                    <Typography variant="caption" sx={{ mt: 0.5 }}>
                      📍 {newDefectData.location}
                    </Typography>
                    <Typography variant="caption">
                      📱 {newDefectData.deviceId} • 🎯 {(newDefectData.confidence * 100).toFixed(1)}% confidence
                    </Typography>
                    <Typography variant="caption">
                      📊 ID: F-{newDefectData.id} • ⏰ {new Date(newDefectData.detectedAt).toLocaleTimeString()}
                    </Typography>
                  </>
                )}
              </Box>
            </Alert>
          </Snackbar>

          {/* Action Feedback Snackbar */}
          <Snackbar
            open={actionSnackbarOpen}
            autoHideDuration={3000}
            onClose={handleCloseActionSnackbar}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          >
            <Alert
              onClose={handleCloseActionSnackbar}
              severity={actionMessage.includes('Failed') ? 'error' : 'success'}
              sx={{ width: '100%' }}
            >
              {actionMessage}
            </Alert>
          </Snackbar>

          {/* Defect Validation Modal */}
          <DefectValidationModal
            open={modalOpen}
            onClose={handleCloseModal}
            selectedDefect={selectedDefect}
            defectTypesList={defectTypesList}
            currentDefectTypeId={currentDefectTypeId}
            onDefectTypeChange={handleDefectTypeChange}
            rejectionReason={rejectionReason}
            onRejectionReasonChange={(e) => setRejectionReason(e.target.value)}
            loadingDefectTypes={loadingDefectTypes}
            onValidate={handleValidateDefect}
            onReject={handleRejectDefect}
            onRevertToDraft={handleRevertToDraft}
            formatDateTime={formatDateTime}
            viewOnly={selectedDefect?.recordType === 'confirmed_defect' || selectedDefect?.validationStatus === 'validated'}
            canRevert={
              canEdit &&
              user?.role &&
              ['manager', 'system_admin'].includes(user.role) &&
              (selectedDefect?.validationStatus === 'validated' || selectedDefect?.recordType === 'confirmed_defect')
            }
          />

          {/* Add Payload Modal */}
          <Modal
            open={payloadModalOpen}
            onClose={handleClosePayloadModal}
            aria-labelledby="payload-modal-title"
            aria-describedby="payload-modal-description"
          >
            <Box sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: 600,
              bgcolor: 'background.paper',
              borderRadius: '12px',
              boxShadow: 24,
              p: 4,
            }}>
              <Typography id="payload-modal-title" variant="h6" component="h2" sx={{ mb: 2, fontWeight: 600 }}>
                Add JSON Payload
              </Typography>
              
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Upload a JSON file or paste JSON content for debugging/testing purposes.
              </Typography>

              {/* File Upload Section */}
              <Box sx={{ mb: 3 }}>
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<UploadFileIcon />}
                  sx={{ mb: 2 }}
                >
                  Upload JSON File
                  <input
                    type="file"
                    accept=".json"
                    hidden
                    onChange={handleFileUpload}
                  />
                </Button>
                {payloadFile && (
                  <Typography variant="body2" color="text.secondary">
                    Selected file: {payloadFile.name}
                  </Typography>
                )}
              </Box>

              <Divider sx={{ mb: 3 }}>OR</Divider>

              {/* Text Input Section */}
              <TextField
                fullWidth
                multiline
                rows={12}
                label="JSON Payload"
                value={payloadText}
                onChange={handlePayloadTextChange}
                placeholder='{\n  "example": "payload",\n  "data": {\n    "key": "value"\n  }\n}'
                sx={{ 
                  mb: 3,
                  '& .MuiInputBase-input': {
                    fontFamily: 'monospace',
                    fontSize: '0.875rem'
                  }
                }}
                helperText="Paste or type your JSON payload here"
              />

              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                <Button 
                  onClick={handleClosePayloadModal} 
                  variant="outlined"
                  sx={{ minWidth: '100px' }}
                >
                  Cancel
                </Button>
                <Button 
                  variant="contained" 
                  color="primary"
                  onClick={handleSubmitPayload}
                  disabled={!payloadText.trim() || payloadProcessing}
                  sx={{ minWidth: '100px' }}
                  startIcon={payloadProcessing ? <CircularProgress size={16} color="inherit" /> : null}
                >
                  {payloadProcessing ? 'Processing...' : 'Submit'}
                </Button>
              </Box>
            </Box>
          </Modal>

          {/* Duplication Modal */}
          <DuplicationModal
            open={duplicationModalOpen}
            onClose={handleCloseDuplicationModal}
            selectedRowForDuplicates={selectedRowForDuplicates}
            duplicateImages={duplicateImages}
            loadingDuplicates={loadingDuplicates}
            formatDateTime={formatDateTime}
          />

          {/* Work Order Modal */}
          <IssueWorkOrderModal
            open={workOrderModalOpen}
            onClose={handleCloseWorkOrderModal}
            defectData={selectedDefectForWorkOrder}
            onWorkOrderCreated={handleWorkOrderCreated}
          />

          {/* Delete Confirmation Dialog */}
          <Dialog
            open={deleteDialogOpen}
            onClose={handleCloseDeleteDialog}
            aria-labelledby="delete-dialog-title"
            aria-describedby="delete-dialog-description"
            PaperProps={{
              sx: {
                borderRadius: '12px',
                minWidth: '400px'
              }
            }}
          >
            <DialogTitle 
              id="delete-dialog-title"
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1,
                pb: 1
              }}
            >
              <WarningIcon color="error" />
              Delete {selectedRowForDelete?.recordType === 'confirmed_defect' ? 'Defect' : 'Finding'}
            </DialogTitle>
            <DialogContent>
              <DialogContentText id="delete-dialog-description" sx={{ mb: 2 }}>
                Are you sure you want to permanently delete this{' '}
                {selectedRowForDelete?.recordType === 'confirmed_defect' ? 'defect' : 'finding'}?
                This action cannot be undone.
              </DialogContentText>
              
              {selectedRowForDelete && (
                <Box sx={{ 
                  bgcolor: alpha(theme.palette.error.main, 0.05),
                  border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
                  borderRadius: 2,
                  p: 2,
                  mt: 2
                }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'error.main' }}>
                    {selectedRowForDelete.recordType === 'confirmed_defect' ? 'Defect' : 'Finding'} Details:
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <strong>ID:</strong> {selectedRowForDelete.recordType === 'confirmed_defect' 
                      ? `D-${selectedRowForDelete.defectId}` 
                      : `F-${selectedRowForDelete.id}`}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <strong>Type:</strong> {selectedRowForDelete.defectType}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <strong>Device:</strong> {selectedRowForDelete.deviceId}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <strong>Location:</strong> {selectedRowForDelete.isOutOfBoundary 
                      ? `Out of Boundary (${selectedRowForDelete.coordinates})` 
                      : selectedRowForDelete.location}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Detected:</strong> {formatDateTime(selectedRowForDelete.detectedAt)}
                  </Typography>
                </Box>
              )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 3 }}>
              <Button 
                onClick={handleCloseDeleteDialog}
                variant="outlined"
                disabled={deleting}
              >
                Cancel
              </Button>
              <Button 
                onClick={handleConfirmDelete}
                variant="contained"
                color="error"
                disabled={deleting}
                startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />}
                sx={{ minWidth: '120px' }}
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Custom Date Range Dialog */}
          <Dialog
            open={customDateRangeOpen}
            onClose={handleCustomDateRangeClose}
            aria-labelledby="custom-date-range-dialog-title"
            PaperProps={{
              sx: {
                borderRadius: '12px',
                minWidth: '400px',
                maxWidth: '500px'
              }
            }}
          >
            <DialogTitle 
              id="custom-date-range-dialog-title"
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1,
                pb: 1
              }}
            >
              <DateRangeIcon color="primary" />
              Custom Date & Time Range
            </DialogTitle>
            <DialogContent sx={{ pt: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Select a custom date and time range to filter defects. Leave either field empty for open-ended ranges.
              </Typography>
              
              <Stack spacing={3}>
                <DateTimePicker
                  label="From Date & Time"
                  value={customFromDate}
                  onChange={(newValue) => setCustomFromDate(newValue)}
                  format="dd/MM/yyyy HH:mm"
                  ampm={false}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: 'small',
                      sx: {
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '8px',
                          fontSize: '0.875rem',
                        }
                      }
                    }
                  }}
                  maxDateTime={customToDate || undefined}
                />
                
                <DateTimePicker
                  label="To Date & Time"
                  value={customToDate}
                  onChange={(newValue) => setCustomToDate(newValue)}
                  format="dd/MM/yyyy HH:mm"
                  ampm={false}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: 'small',
                      sx: {
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '8px',
                          fontSize: '0.875rem',
                        }
                      }
                    }
                  }}
                  minDateTime={customFromDate || undefined}
                />
              </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
              <Button 
                onClick={handleCustomDateRangeClear}
                variant="outlined"
                color="error"
                size="small"
                sx={{ 
                  borderRadius: '8px',
                  textTransform: 'none',
                  fontSize: '0.875rem'
                }}
              >
                Clear
              </Button>
              <Button 
                onClick={handleCustomDateRangeClose}
                variant="outlined"
                size="small"
                sx={{ 
                  borderRadius: '8px',
                  textTransform: 'none',
                  fontSize: '0.875rem'
                }}
              >
                Cancel
              </Button>
              <Button 
                onClick={handleCustomDateRangeApply}
                variant="contained"
                color="primary"
                size="small"
                disabled={!customFromDate && !customToDate}
                sx={{ 
                  borderRadius: '8px',
                  textTransform: 'none',
                  fontSize: '0.875rem',
                  minWidth: '80px'
                }}
              >
                Apply
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </Box>
    </DisciplineAccessControl>
  );
};

export default DefectsPage;